import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { FileDropzone } from "@/components/file-dropzone";
import { useToast } from "@/hooks/use-toast";
import { Banknote, Loader2, Trash2, Upload, Receipt, CreditCard, FileText, ChevronRight, ChevronDown, Link2, Link2Off, CheckCircle2, AlertCircle, AlertTriangle, PlayCircle, RefreshCw, Plug, ArrowLeftRight, ArrowRight, Settings, History, Lock, Wallet, Archive, ArchiveRestore, Filter, X, ArrowUp, ArrowDown, ArrowUpDown, Mail, Search, MapPin, Building2, Send } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useAuth } from "@/hooks/use-auth";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

// Sentinel resolvedAccount for a remittance whose lines settle invoices on
// several Merlin accounts. Posting groups by each line's own account; the
// sentinel is never handed to Merlin. Kept in sync with the server-side
// MULTI_ACCOUNT_SENTINEL in api-server/src/lib/cashReceiptConstants.ts.
const MULTI_ACCOUNT_SENTINEL = "(multiple)";

interface BankStatementUpload {
  id: number;
  originalFilename: string;
  source: "barclays_dcs" | "barclays_current";
  accountLabel: string;
  nominal: number | null;
  statementDate: string | null;
  paymentCount: number;
  totalReceived: number;
  createdAt: string;
  archivedAt: string | null;
  archivedReason: string | null;
}

interface MerlinSubmission {
  invoiceRef: string;
  sourceRef?: string;
  sltranid: string;
  customerid: string;
  // Merlin account this invoice was booked under. Optional for rows posted
  // before per-invoice account was recorded. For a multi-account remittance
  // different submissions carry different accounts.
  account?: string;
  nominal?: number;
  amount: number;
  reference: string;
  code: string;
  message: string;
  postedAt: string;
}

// One forensic line for a submission tooltip, prefixing the Merlin account when
// it's known so a multi-account post shows which account each invoice hit.
function fmtSubmission(s: MerlinSubmission): string {
  const acct = s.account ? `[${s.account}] ` : "";
  const nominal = s.nominal ? `[bank nominal ${s.nominal}] ` : "";
  return `${acct}${nominal}${s.invoiceRef}: ${s.code === "0" ? "ok" : `${s.code} ${s.message}`}`;
}

interface BankPayment {
  id: number;
  updatedAt: string;
  uploadId: number;
  paymentDate: string;
  amount: number;
  narrative: string;
  customerHintAccount: string | null;
  customerHintInvoice: string | null;
  customerHintEftRef: string | null;
  status: string;
  resolvedAccount: string | null;
  accountResolvedBy: string | null;
  allocatedInvoices: Array<{ ref: string; balance: number }> | null;
  matchedRemittanceId: number | null;
  remittanceRequestedAt: string | null;
  replyToken: string | null;
  merlinPostingStartedAt: string | null;
  merlinPostedAt: string | null;
  merlinSubmissions: MerlinSubmission[] | null;
  replyReceivedAt: string | null;
  replyText: string | null;
  replyAllocationSource: "explicit" | "rule" | "split" | "unclear" | null;
  replyParseNotes: string | null;
  // Non-null when the payment cannot be posted yet despite looking resolved.
  // Set for a matched, multi-account remittance with line(s) that have no
  // resolved Merlin account — posting would fail server-side, so the Post
  // button is disabled and this string explains why.
  postBlockedReason: string | null;
  // Flagged remittance lines on this payment's matched remittance (persistent
  // merlin_account_warning_code). null/empty when nothing is flagged. high-risk
  // ones soft-gate posting (require "post anyway"); the rest are advisory.
  warningLines: WarningLine[] | null;
  // Whole-payment on-account wait gate. null when this payment isn't in the
  // "Post on-account" state. When present and blocked, the unallocated post is
  // not yet allowed: a remittance must have been requested and 48h must have
  // passed with no reply. reason explains why; eligibleAt is when it unblocks.
  onAccountWait: { blocked: boolean; reason: string | null; eligibleAt: string | null } | null;
}

export type PostToMerlinVariables = {
  paymentId: number;
  forceOnAccount?: boolean;
  acknowledgeWarnings?: boolean;
  overrideRemittanceWait?: boolean;
};

/**
 * "Post on-account" is an explicit operator allocation choice, not permission
 * for the server to substitute a late invoice match. Carry forceOnAccount on
 * every retry/soft-gate path so transactionid=-1 remains the actual outcome.
 */
export function postToMerlinVariables(
  payment: Pick<BankPayment, "id" | "status" | "allocatedInvoices">,
): PostToMerlinVariables {
  const isOnAccount =
    (payment.status === "needs_review" || payment.status === "awaiting_remittance") &&
    (!payment.allocatedInvoices || payment.allocatedInvoices.length === 0);
  return {
    paymentId: payment.id,
    ...(isOnAccount ? { forceOnAccount: true } : {}),
  };
}

/**
 * A bank payment no longer needs operator action once it has either been
 * posted by this app, explicitly excluded/ignored, or Merlin confirms that
 * every allocated invoice was already settled elsewhere.
 *
 * Keep this aligned with the API's statement completion tally.
 */
export function isBankPaymentDone(
  p: Pick<BankPayment, "merlinPostedAt" | "status"> & Partial<Pick<BankPayment, "merlinSubmissions">>,
): boolean {
  const latestSubmissions = latestMerlinSubmissions(p.merlinSubmissions ?? []);
  const terminalPartial = !!p.merlinPostedAt
    && latestSubmissions.some((s) => s.code === "0")
    && latestSubmissions.some((s) => s.code !== "0");
  return (
    (!!p.merlinPostedAt && !terminalPartial) ||
    p.status === "excluded" ||
    p.status === "ignored" ||
    p.status === "already_settled"
  );
}

export function bankPaymentMatchesStatusFilter(
  p: Pick<BankPayment, "merlinPostedAt" | "status"> & Partial<Pick<BankPayment, "merlinSubmissions">>,
  filter: string,
): boolean {
  if (filter === "all") return true;
  if (filter === "hide_posted") return !isBankPaymentDone(p);
  if (filter === "hide_matched") return p.status !== "matched" && p.status !== "excluded";
  return p.status === filter;
}

function latestMerlinSubmissions(submissions: MerlinSubmission[]): MerlinSubmission[] {
  const byRef = new Map<string, MerlinSubmission>();
  for (const submission of submissions) {
    byRef.set(submission.sourceRef ?? submission.invoiceRef, submission);
  }
  return [...byRef.values()];
}

interface WarningLine {
  lineId: number;
  reference: string;
  account: string | null;
  warningCode: string;
  warning: string | null;
  highRisk: boolean;
}

function AccountEditor({
  payment,
  disabled,
  onSave,
}: {
  payment: BankPayment;
  disabled: boolean;
  onSave: (account: string | null) => void;
}) {
  const [value, setValue] = useState(payment.resolvedAccount ?? "");
  const initial = payment.resolvedAccount ?? "";
  const dirty = value.trim() !== initial.trim();
  const handleSave = () => {
    const trimmed = value.trim();
    onSave(trimmed === "" ? null : trimmed.toUpperCase());
  };
  return (
    <div className="inline-flex items-center gap-1">
      <span className="text-duffells-grey/50">→ acct:</span>
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && dirty && !disabled) handleSave();
          if (e.key === "Escape") setValue(initial);
        }}
        placeholder="e.g. HGL001"
        disabled={disabled}
        className="font-mono text-xs px-1.5 py-0.5 border border-slate-200 rounded w-28 focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
      {dirty && (
        <button
          onClick={handleSave}
          disabled={disabled}
          className="text-xs px-1.5 py-0.5 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {disabled ? "…" : "Save"}
        </button>
      )}
      {!dirty && payment.accountResolvedBy && payment.resolvedAccount && (
        <span
          className="text-[10px] px-1 py-0.5 rounded bg-slate-100 text-slate-500"
          title={`Resolved via ${payment.accountResolvedBy}`}
        >
          {payment.accountResolvedBy}
        </span>
      )}
    </div>
  );
}

// The per-payment Merlin-account cell. Mirrors the per-line LineAccountCell
// treatment: once the payment has begun posting to Merlin
// (merlinPostingStartedAt set) the account is baked into what reached Merlin,
// so we show a read-only locked affordance instead of the AccountEditor (which
// would 409 server-side). When the payment is matched/auto_allocated but not
// yet posting the account is load-bearing too, so it's shown static. Only an
// un-posted, unmatched payment exposes the editable AccountEditor.
export const PAYMENT_ACCOUNT_LOCK_TIP =
  "This payment has already started posting to Merlin — its customer account is locked and can't be changed.";

export function PaymentAccountCell({
  payment,
  disabled,
  onSave,
}: {
  payment: BankPayment;
  disabled: boolean;
  onSave: (account: string | null) => void;
}) {
  const accountLocked = !!payment.merlinPostingStartedAt;
  const accountEditable =
    !accountLocked && payment.status !== "matched" && payment.status !== "auto_allocated";

  if (accountLocked) {
    return (
      <div className="inline-flex items-center gap-1.5" title={PAYMENT_ACCOUNT_LOCK_TIP}>
        <span className="text-duffells-grey/50">→ acct:</span>
        {payment.resolvedAccount === MULTI_ACCOUNT_SENTINEL ? (
          <span className="font-medium text-amber-700">multiple accounts</span>
        ) : payment.resolvedAccount ? (
          <span className="font-mono text-emerald-700">{payment.resolvedAccount}</span>
        ) : (
          <span className="text-slate-400">none</span>
        )}
        <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5">
          <Lock className="w-2.5 h-2.5" />
          locked
        </span>
      </div>
    );
  }

  if (accountEditable) {
    return <AccountEditor payment={payment} disabled={disabled} onSave={onSave} />;
  }

  return (
    payment.resolvedAccount ? (
      payment.resolvedAccount === MULTI_ACCOUNT_SENTINEL ? (
        <div><span className="text-duffells-grey/50">→ acct:</span> <span className="font-medium text-amber-700">multiple accounts</span></div>
      ) : (
        <div><span className="text-duffells-grey/50">→ acct:</span> <span className="font-mono text-emerald-700">{payment.resolvedAccount}</span></div>
      )
    ) : null
  );
}

// Inline Merlin-account editor for a single unresolved remittance line. Mirrors
// the per-payment AccountEditor pattern but targets one line so an operator can
// resolve a stray line without re-running the whole match. Saving clears the
// multi-account post gate server-side once every line carries an account.
function LineAccountEditor({
  line,
  disabled,
  onSave,
}: {
  line: RemittanceLine;
  disabled: boolean;
  onSave: (account: string | null) => void;
}) {
  const initial = line.merlinAccount ?? "";
  const [value, setValue] = useState(initial);
  const dirty = value.trim().toUpperCase() !== initial.trim().toUpperCase();
  const handleSave = () => {
    const trimmed = value.trim();
    onSave(trimmed === "" ? null : trimmed.toUpperCase());
  };
  return (
    <div className="inline-flex items-center gap-1">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && dirty && !disabled) handleSave();
          if (e.key === "Escape") setValue(initial);
        }}
        onClick={(e) => e.stopPropagation()}
        placeholder="set account"
        disabled={disabled}
        className="font-mono text-xs px-1.5 py-0.5 border border-slate-300 rounded w-24 focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
      {dirty && (
        <button
          onClick={(e) => { e.stopPropagation(); handleSave(); }}
          disabled={disabled}
          className="text-xs px-1.5 py-0.5 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
        >
          {disabled ? "…" : "Save"}
        </button>
      )}
    </div>
  );
}

// Reference cell for a single remittance line, with an inline editor. Customers
// sometimes mistype an invoice/credit number (e.g. an extra leading digit) so
// the line lands "ref not found" and no account rule can fix it. The "edit"
// affordance lets an operator correct the reference; saving re-verifies it
// against the line's existing account server-side, clearing the badge in place.
export function LineReferenceCell({
  line,
  disabled,
  locked,
  onSave,
}: {
  line: RemittanceLine;
  disabled: boolean;
  // True once the matched payment has begun posting to Merlin — the reference is
  // baked into what reached Merlin, so it's shown read-only.
  locked: boolean;
  onSave: (reference: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(line.reference);
  const suppliedReference = line.reference.trim();
  const inferredReference = line.inferredReference?.trim() || null;
  const recovered =
    !!inferredReference &&
    inferredReference.toUpperCase() !== suppliedReference.toUpperCase();
  const unresolved = !!line.merlinCheckedAt && !!line.merlinNotFound;

  // Keep the draft in sync if the line's reference changes underneath us after a
  // successful save invalidates and refetches the detail.
  useEffect(() => { setValue(line.reference); }, [line.reference]);

  if (locked || !editing) {
    return (
      <div className="inline-flex items-center gap-1.5">
        {recovered ? (
          <div className="flex flex-col leading-tight">
            <span
              className="font-mono font-semibold text-emerald-700"
              title="Canonical Merlin reference recovered from the customer-supplied reference"
            >
              {inferredReference}
            </span>
            <span className="text-[10px] text-slate-500">
              supplied: <span className="font-mono">{line.reference}</span>
            </span>
          </div>
        ) : (
          <span className="font-mono">{line.reference}</span>
        )}
        {recovered ? (
          <span
            className="text-[10px] px-1 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
            title="The canonical Merlin reference was inferred automatically"
          >
            recovered
          </span>
        ) : unresolved ? (
          <span
            className="text-[10px] px-1 py-0.5 rounded bg-red-50 text-red-700 border border-red-200"
            title="This customer-supplied reference was checked but not found in Merlin"
          >
            unresolved
          </span>
        ) : null}
        {!locked && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setValue(line.reference); setEditing(true); }}
            className="text-[10px] text-slate-400 hover:text-primary underline"
            title="Correct the customer's reference for this line (e.g. a mistyped invoice number)"
          >
            edit
          </button>
        )}
      </div>
    );
  }

  const trimmed = value.trim();
  const dirty = trimmed !== "" && trimmed !== line.reference;
  const submit = () => {
    if (dirty && !disabled) onSave(trimmed);
    setEditing(false);
  };
  return (
    <div className="inline-flex items-center gap-1">
      <input
        type="text"
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); submit(); }
          if (e.key === "Escape") { setValue(line.reference); setEditing(false); }
        }}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
        className="font-mono text-xs px-1.5 py-0.5 border border-slate-300 rounded w-32 focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); submit(); }}
        disabled={disabled || !dirty}
        className="text-xs px-1.5 py-0.5 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-50"
      >
        {disabled ? "…" : "Save"}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setValue(line.reference); setEditing(false); }}
        disabled={disabled}
        className="text-[10px] text-slate-400 hover:text-primary underline disabled:opacity-50"
      >
        cancel
      </button>
    </div>
  );
}

export function MerlinAmountCorrectionButton({
  line,
  locked,
  disabled,
  onUse,
}: {
  line: RemittanceLine;
  locked: boolean;
  disabled: boolean;
  onUse: () => void;
}) {
  const merlinTotal = line.merlinInvoiceTotal;
  if (
    locked ||
    merlinTotal == null ||
    Math.abs(Math.abs(line.amount) - Math.abs(merlinTotal)) <= 0.01
  ) {
    return null;
  }
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        if (!confirm(
          `Replace ${line.reference}'s parsed amount ${fmtMoney(line.amount)} with the verified Merlin invoice total ` +
          `${fmtMoney(merlinTotal)}?\n\nThe remittance total will be recalculated and matching retried.`,
        )) return;
        onUse();
      }}
      className="mt-0.5 text-[10px] text-amber-700 hover:text-amber-900 underline disabled:opacity-50"
      title="Use the verified Merlin invoice total, recalculate this remittance, and retry matching"
    >
      {disabled ? "correcting…" : `use Merlin ${fmtMoney(merlinTotal)}`}
    </button>
  );
}

export function InferredReferenceHint({
  reference,
  inferredReference,
}: {
  reference: string;
  inferredReference: string | null;
}) {
  const inferred = inferredReference?.trim();
  if (!inferred || inferred.toUpperCase() === reference.trim().toUpperCase()) return null;
  return (
    <div
      className="flex items-center gap-1 text-[10px] text-emerald-700"
      title={`Customer supplied ${reference}; matched in Merlin as ${inferred}`}
    >
      <span className="text-duffells-grey/60">Matched as</span>
      <span className="font-mono font-semibold">{inferred}</span>
    </div>
  );
}

// One change in a remittance line's Merlin-account history (from the audit
// trail; see the GET /lines/:lineId/account-history route).
interface LineAccountChange {
  // "set" = the account was changed (previous→new arrow); "recheck" = the
  // account was re-verified in place without changing (no arrow).
  kind: "set" | "recheck";
  at: string | null;
  actorName: string | null;
  actorEmail: string | null;
  previousAccount: string | null;
  newAccount: string | null;
  warning: string | null;
  // Re-check fields.
  account: string | null;
  warningBefore: string | null;
  warningAfter: string | null;
}

// Popover showing the full chain of who changed a line's Merlin account, when,
// and from/to what. Sourced read-only from the audit trail; lazily fetched the
// first time it's opened. Matched (automatic) sets leave no audit entry, so an
// untouched auto-resolved line shows "No manual changes recorded".
function LineAccountHistory({
  remittanceId,
  lineId,
  onClose,
}: {
  remittanceId: number;
  lineId: number;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["line-account-history", remittanceId, lineId],
    queryFn: async () => {
      const res = await fetch(
        `${BASE}/api/cash-receipts/remittances/${remittanceId}/lines/${lineId}/account-history`,
        { credentials: "include" },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body as { lineId: number; history: LineAccountChange[] };
    },
  });
  const history = data?.history ?? [];
  return (
    <div
      className="absolute z-20 mt-1 left-0 top-full w-72 rounded-md border border-slate-200 bg-white shadow-lg p-2 text-left"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-semibold text-slate-600">Account change history</span>
        <button onClick={onClose} className="text-[11px] text-slate-400 hover:text-slate-600">close</button>
      </div>
      {isLoading && <div className="text-[11px] text-slate-400 py-1">Loading…</div>}
      {isError && <div className="text-[11px] text-red-600 py-1">Couldn't load history.</div>}
      {!isLoading && !isError && history.length === 0 && (
        <div className="text-[11px] text-slate-400 py-1">No manual changes recorded.</div>
      )}
      {!isLoading && !isError && history.length > 0 && (
        <ol className="space-y-1.5">
          {history.map((h, i) =>
            h.kind === "recheck" ? (
              <li key={i} className="text-[11px] text-slate-600 border-l-2 border-sky-200 pl-2">
                <div className="font-mono text-sky-700">
                  re-checked {h.account ?? "∅"}
                </div>
                <div className="text-slate-400">
                  {h.actorName || h.actorEmail || "unknown"}
                  {h.at ? ` · ${fmtDate(h.at)}` : ""}
                </div>
                {h.warningAfter ? (
                  <div className="text-amber-600 mt-0.5">{h.warningAfter}</div>
                ) : (
                  <div className="text-emerald-600 mt-0.5">warning cleared</div>
                )}
              </li>
            ) : (
              <li key={i} className="text-[11px] text-slate-600 border-l-2 border-slate-200 pl-2">
                <div className="font-mono">
                  <span className="text-slate-400">{h.previousAccount ?? "∅"}</span>
                  <span className="mx-1">→</span>
                  <span className="text-emerald-700">{h.newAccount ?? "∅"}</span>
                </div>
                <div className="text-slate-400">
                  {h.actorName || h.actorEmail || "unknown"}
                  {h.at ? ` · ${fmtDate(h.at)}` : ""}
                </div>
                {h.warning && <div className="text-amber-600 mt-0.5">{h.warning}</div>}
              </li>
            ),
          )}
        </ol>
      )}
    </div>
  );
}

// Persistent warning badge for a remittance line whose inline-set Merlin
// account didn't verify cleanly. Driven by the server-persisted
// merlinAccountWarningCode (kept in sync with the PATCH handler in
// cashReceipts.ts) so the warning survives reloads — not just the save toast.
const LINE_WARNING_META: Record<string, { label: string; className: string }> = {
  ownership_mismatch: { label: "wrong account", className: "bg-red-100 text-red-700 border-red-300" },
  reference_not_found: { label: "ref not found", className: "bg-red-100 text-red-700 border-red-300" },
  // Set on a line by a partial post when its ref couldn't be matched to a single
  // postable Merlin invoice (settled, ambiguous, sign-mismatch, …). The line was
  // routed to review: fix the ref / match an invoice, or pay it unallocated.
  merlin_unmatched: { label: "needs review", className: "bg-red-100 text-red-700 border-red-300" },
  amount_mismatch: { label: "amount mismatch", className: "bg-amber-100 text-amber-800 border-amber-300" },
  ambiguous_account: { label: "ambiguous account", className: "bg-amber-100 text-amber-800 border-amber-300" },
  merlin_unreachable: { label: "unverified", className: "bg-slate-100 text-slate-600 border-slate-300" },
};

function LineAccountWarningBadge({ line }: { line: RemittanceLine }) {
  const code = line.merlinAccountWarningCode;
  if (!code) return null;
  const meta = LINE_WARNING_META[code] ?? { label: "warning", className: "bg-amber-100 text-amber-800 border-amber-300" };
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border whitespace-nowrap ${meta.className}`}
      title={line.merlinAccountWarning ?? meta.label}
    >
      <AlertTriangle className="w-3 h-3 shrink-0" />
      {meta.label}
    </span>
  );
}

// Merlin-account cell for a single remittance line. An unresolved line shows the
// "not checked"/"not found" state plus the inline editor; a resolved (green) line
// shows the account with an "edit" affordance that swaps in the same editor so an
// operator can overwrite a wrongly-matched account without re-running Match.
// Saving hits the same per-line PATCH route, so the multi-account split/gate
// re-runs on the invalidated query exactly like the unresolved path.
function LineAccountCell({
  line,
  remittanceId,
  flagUnresolved,
  disabled,
  locked,
  onSave,
  onRecheck,
  rechecking,
}: {
  line: RemittanceLine;
  remittanceId: number;
  flagUnresolved: boolean;
  disabled: boolean;
  // True once the matched payment has begun posting to Merlin. The account is
  // baked into what reached Merlin, so the field is shown read-only with an
  // explanation rather than an editor that would fail server-side with a 409.
  locked: boolean;
  onSave: (account: string | null) => void;
  onRecheck: () => void;
  rechecking: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const checked = !!line.merlinCheckedAt;
  const lockTip =
    "This payment has already started posting to Merlin — its line accounts are locked and can't be changed.";

  if (locked) {
    return (
      <div className="inline-flex items-center gap-1.5" title={lockTip}>
        {line.merlinAccount ? (
          <span className="text-emerald-700">{line.merlinAccount}</span>
        ) : (
          <span className="text-slate-400">{!checked ? "not checked" : "not found"}</span>
        )}
        <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5">
          <Lock className="w-2.5 h-2.5" />
          locked
        </span>
      </div>
    );
  }

  if (line.merlinAccount && !editing) {
    const manual = line.accountSetBy === "manual";
    const when = line.accountSetAt ? fmtDate(line.accountSetAt) : null;
    const tip = manual
      ? `Set manually${line.accountSetByName ? ` by ${line.accountSetByName}` : ""}${when ? ` on ${when}` : ""}`
      : `Resolved automatically by matching${when ? ` on ${when}` : ""}`;
    const warned = !!line.merlinAccountWarningCode;
    return (
      <div className="relative inline-flex items-center gap-1.5 flex-wrap">
        <span className={warned ? "text-amber-700 font-medium" : "text-emerald-700"}>{line.merlinAccount}</span>
        {line.accountSetBy && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowHistory((s) => !s); }}
            className={`text-[10px] px-1 py-0.5 rounded inline-flex items-center gap-0.5 ${manual ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
            title={`${tip} — click for full change history`}
          >
            {manual ? "manual" : "matched"}
            <History className="w-2.5 h-2.5" />
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); setEditing(true); }}
          className="text-[10px] text-slate-400 hover:text-primary underline"
          title="Change the Merlin account for this line"
        >
          edit
        </button>
        {showHistory && (
          <LineAccountHistory
            remittanceId={remittanceId}
            lineId={line.id}
            onClose={() => setShowHistory(false)}
          />
        )}
        <LineAccountWarningBadge line={line} />
        {warned && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRecheck(); }}
            disabled={disabled || rechecking}
            className="text-[10px] text-slate-400 hover:text-primary underline disabled:opacity-50"
            title="Re-run the Merlin check for this account without clearing it"
          >
            {rechecking ? "checking…" : "re-check"}
          </button>
        )}
      </div>
    );
  }

  if (line.merlinAccount && editing) {
    return (
      <div className="inline-flex items-center gap-1">
        <LineAccountEditor
          line={line}
          disabled={disabled}
          onSave={(account) => { onSave(account); setEditing(false); }}
        />
        <button
          onClick={(e) => { e.stopPropagation(); setEditing(false); }}
          disabled={disabled}
          className="text-[10px] text-slate-400 hover:text-slate-600 disabled:opacity-50"
        >
          cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <span className={flagUnresolved ? "text-red-700 font-semibold inline-flex items-center gap-1" : "text-slate-400"}>
        {flagUnresolved && <AlertCircle className="w-3 h-3" />}{!checked ? "not checked" : "not found"}
      </span>
      <LineAccountEditor
        line={line}
        disabled={disabled}
        onSave={onSave}
      />
    </div>
  );
}

// Manual "match line to outstanding invoice" fallback for an unrecoverable
// reference typo (customer wrote a number that doesn't exist in Merlin). Lists
// the account's actual outstanding invoices/credit notes — the same set the
// auto-allocator draws from — so the operator can pick the real one. The pick
// sets the line's reference + account and re-verifies, exactly like the inline
// editors, so it flows through to Merlin at post time.
function InvoiceMatchPicker({
  remittanceId,
  line,
  disabled,
  onMatched,
}: {
  remittanceId: number;
  line: RemittanceLine;
  disabled: boolean;
  onMatched: (reference: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["invoice-candidates", remittanceId, line.id],
    queryFn: async () => {
      const res = await fetch(
        `${BASE}/api/cash-receipts/remittances/${remittanceId}/lines/${line.id}/invoice-candidates`,
        { credentials: "include" },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body as { account: string; candidates: { ref: string; balance: number }[] };
    },
    enabled: open,
    staleTime: 30_000,
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="text-[10px] text-amber-600 hover:text-amber-800 underline whitespace-nowrap"
        title="Pick a real outstanding invoice from this account's Merlin ledger to match this line to"
      >
        match invoice
      </button>
    );
  }

  const filter = q.trim().toUpperCase();
  const candidates = (data?.candidates ?? []).filter((c) => !filter || c.ref.toUpperCase().includes(filter));

  return (
    <div
      className="absolute z-20 mt-1 left-0 top-full w-80 rounded-md border border-slate-200 bg-white shadow-lg p-2 text-left"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-semibold text-slate-600">
          Outstanding invoices{data?.account ? ` · ${data.account}` : ""}
        </span>
        <button onClick={() => setOpen(false)} className="text-[11px] text-slate-400 hover:text-slate-600">close</button>
      </div>
      <input
        type="text"
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Filter by reference…"
        className="w-full mb-2 font-mono text-xs px-1.5 py-1 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
      {isLoading && <div className="text-[11px] text-slate-400 py-1">Loading from Merlin…</div>}
      {isError && <div className="text-[11px] text-red-600 py-1">{(error as Error)?.message || "Couldn't load candidates."}</div>}
      {!isLoading && !isError && candidates.length === 0 && (
        <div className="text-[11px] text-slate-400 py-1">{filter ? "No matching references." : "No outstanding invoices on this account."}</div>
      )}
      {!isLoading && !isError && candidates.length > 0 && (
        <ul className="max-h-56 overflow-auto divide-y divide-slate-100">
          {candidates.map((c) => (
            <li key={c.ref}>
              <button
                type="button"
                disabled={disabled}
                onClick={(e) => { e.stopPropagation(); onMatched(c.ref); setOpen(false); }}
                className="w-full flex justify-between items-center gap-2 px-1.5 py-1 text-xs hover:bg-primary/5 rounded disabled:opacity-50"
                title={`Match this line to ${c.ref}`}
              >
                <span className="font-mono text-duffells-grey">{c.ref}</span>
                <span className={`font-mono ${c.balance < 0 ? "text-red-600" : "text-duffells-grey/70"}`}>{fmtMoney(c.balance)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function statusPill(status: string) {
  const map: Record<string, string> = {
    unmatched: "bg-slate-100 text-slate-700",
    matched: "bg-emerald-100 text-emerald-700",
    auto_allocated: "bg-emerald-100 text-emerald-700",
    awaiting_remittance: "bg-sky-100 text-sky-700",
    awaiting_review: "bg-violet-100 text-violet-700",
    processing: "bg-slate-100 text-slate-500",
    needs_review: "bg-amber-100 text-amber-700",
    excluded: "bg-slate-100 text-slate-500 line-through",
    ignored: "bg-slate-50 text-slate-400",
    archived: "bg-indigo-100 text-indigo-700",
  };
  const label = status.replace(/_/g, " ");
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${map[status] || map.unmatched}`}>
      {label}
    </span>
  );
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  try { return format(parseISO(iso), "d MMM yyyy"); } catch { return iso; }
}
function fmtMoney(n: number) {
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function CashReceiptsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isSuperAdmin, isRealAdmin } = useAuth();
  const [tab, setTab] = useState<"bacs" | "remittances" | "stripe" | "credit" | "unallocated">("bacs");
  const [bacsSubTab, setBacsSubTab] = useState<"active" | "archived">("active");
  const [uploadsSubTab, setUploadsSubTab] = useState<"active" | "archived">("active");
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [filterUploadId, setFilterUploadId] = useState<number | null>(null);
  const [bankSource, setBankSource] = useState<"barclays_dcs" | "barclays_current">("barclays_dcs");
  const [statusFilter, setStatusFilter] = useState<string>("hide_posted");
  const [pingingMerlin, setPingingMerlin] = useState(false);
  // Deep-link target from the audit log (e.g. ?payment=123). Holds the
  // bank-payment id we should reveal, scroll to and briefly highlight.
  const [focusPaymentId, setFocusPaymentId] = useState<number | null>(null);
  const focusHandledRef = useRef<number | null>(null);
  // In-page "Resolve lines" jump from a blocked BACS row to its matched
  // remittance: switch to the Remittances tab and tell RemittancesTab which
  // remittance to expand, scroll to and highlight. Distinct from the URL
  // deep-link (?remittance=) so it can re-trigger on every click.
  const [resolveRemittanceId, setResolveRemittanceId] = useState<number | null>(null);
  const handleResolveLines = (remittanceId: number) => {
    setTab("remittances");
    setResolveRemittanceId(remittanceId);
  };

  // Parse the deep-link query params once on mount. Remittance targets just
  // switch the tab — RemittancesTab handles its own scroll/highlight.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const remittance = params.get("remittance");
    if (remittance && /^\d+$/.test(remittance)) {
      setTab("remittances");
    } else if (payment && /^\d+$/.test(payment)) {
      setTab("bacs");
      setStatusFilter("all");
      setFocusPaymentId(Number(payment));
    }
  }, []);

  const toggleDate = (date: string) =>
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });

  const testMerlinConnection = async () => {
    setPingingMerlin(true);
    try {
      const res = await fetch(`${BASE}/api/cash-receipts/merlin/soap-ping`, {
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        toast({
          title: "Merlin SOAP connection OK",
          description: `CheckStatus returned code ${data.code}${data.message ? `: ${data.message}` : ""}. The CashReceipt endpoint should be reachable.`,
        });
      } else {
        toast({
          title: "Merlin SOAP connection FAILED",
          description: data?.error || data?.message || `HTTP ${res.status}`,
          variant: "destructive",
        });
      }
    } catch (err: any) {
      toast({
        title: "Merlin SOAP connection FAILED",
        description: err?.message || "Network error",
        variant: "destructive",
      });
    } finally {
      setPingingMerlin(false);
    }
  };

  const { data: uploads = [], isLoading: uploadsLoading } = useQuery<BankStatementUpload[]>({
    queryKey: ["cash-receipts", "uploads"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/bank-statements`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load bank statements");
      return res.json();
    },
  });

  // Active vs. archived statements. A statement auto-archives once it is >14 days
  // old with every payment posted/excluded, and operators can archive/restore any
  // statement manually. The archived view keeps the rows fully accessible.
  const activeUploads = uploads.filter((u) => !u.archivedAt);
  const archivedUploads = uploads.filter((u) => u.archivedAt);
  const visibleUploads = uploadsSubTab === "active" ? activeUploads : archivedUploads;

  const { data: payments = [], isLoading: paymentsLoading } = useQuery<BankPayment[]>({
    queryKey: ["cash-receipts", "payments", filterUploadId],
    queryFn: async () => {
      const url = filterUploadId
        ? `${BASE}/api/cash-receipts/payments?uploadId=${filterUploadId}`
        : `${BASE}/api/cash-receipts/payments`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load payments");
      return res.json();
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", bankSource);
      const res = await fetch(`${BASE}/api/cash-receipts/bank-statements/upload`, {
        method: "POST",
        body: fd,
        credentials: "include",
        // FormData sets its own Content-Type (multipart boundary); we only
        // need the CSRF header the server requires on state-changing requests.
        headers: { "X-Requested-With": "fetch" },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Upload failed");
      }
      return res.json();
    },
    onSuccess: (data: BankStatementUpload) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({ title: `Imported ${data.paymentCount} payments (${fmtMoney(data.totalReceived)})` });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const deleteUploadMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/cash-receipts/bank-statements/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      setFilterUploadId(null);
      toast({ title: "Bank statement deleted" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const archiveUploadMutation = useMutation({
    mutationFn: async ({ id, archived }: { id: number; archived: boolean }) => {
      const res = await fetch(`${BASE}/api/cash-receipts/bank-statements/${id}/archive`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({ archived }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to update statement");
      }
      return res.json() as Promise<BankStatementUpload>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({ title: data.archivedAt ? "Bank statement archived" : "Bank statement restored" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const handleFiles = async (files: File[]) => {
    setUploading(true);
    for (const file of files) {
      try { await uploadMutation.mutateAsync(file); } catch {}
    }
    setUploading(false);
  };

  const handleDelete = (u: BankStatementUpload) => {
    if (!confirm(`Delete bank statement "${u.originalFilename}" and all ${u.paymentCount} payments? This cannot be undone.`)) return;
    deleteUploadMutation.mutate(u.id);
  };

  const postToMerlinMutation = useMutation({
    mutationFn: async ({
      paymentId,
      forceOnAccount,
      acknowledgeWarnings,
      overrideRemittanceWait,
    }: PostToMerlinVariables) => {
      const res = await fetch(`${BASE}/api/cash-receipts/bank-payments/${paymentId}/post-to-merlin`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({
          forceOnAccount: !!forceOnAccount,
          acknowledgeWarnings: !!acknowledgeWarnings,
          overrideRemittanceWait: !!overrideRemittanceWait,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err: any = new Error(body.error || `HTTP ${res.status}`);
        err.paymentId = paymentId;
        // Carry the server's warning-ack signal through so onError can re-prompt
        // with the authoritative flagged-line list (handles stale list data).
        err.requiresWarningAck = body.requiresWarningAck === true;
        err.warningLines = (body.warningLines as WarningLine[] | undefined) ?? [];
        // Authoritative on-account wait gate: server refused because the 48h
        // remittance window hasn't elapsed (or no request was sent). onError
        // offers an explicit override.
        err.requiresWaitOverride = body.requiresWaitOverride === true;
        throw err;
      }
      return body as {
        ok: boolean;
        result: {
          attempted: number;
          succeeded: number;
          failed: number;
          submissions: MerlinSubmission[];
          reviewLines?: Array<{ ref: string; account: string | null; code: string; reason: string }>;
        };
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts", "payments"] });
      const { attempted, succeeded, failed, submissions, reviewLines } = data.result;
      const review = reviewLines ?? [];
      if (data.ok) {
        // Partial post: some lines posted, others routed to review. Surface the
        // count + the refs so the operator knows what's left to resolve.
        if (review.length > 0) {
          toast({
            title: `Posted ${succeeded}/${attempted} — ${review.length} line(s) could not be posted`,
            description: review.map((r) => `${r.ref} — ${r.reason}`).join("; "),
          });
        } else {
          toast({ title: `Posted ${succeeded}/${attempted} invoice(s) to Merlin` });
        }
      } else {
        const firstErr = submissions.find((s) => s.code !== "0");
        toast({
          title: `Merlin posting partially failed (${failed}/${attempted})`,
          description: firstErr ? `Invoice ${firstErr.invoiceRef}: code ${firstErr.code} — ${firstErr.message}` : undefined,
          variant: "destructive",
        });
      }
    },
    onError: (err: any) => {
      // The server refuses on-account when Merlin still has a matching
      // open invoice on the account; surface the message and offer an
      // explicit override so the operator can intentionally post
      // unallocated cash (deposit / overpayment / prepayment).
      const msg = String(err?.message || "");
      // Preserve the flags already set on the failed request so a follow-up
      // retry doesn't drop a previous acknowledgement / override.
      const prevForce = postToMerlinMutation.variables?.forceOnAccount;
      const prevAck = postToMerlinMutation.variables?.acknowledgeWarnings;
      const prevWait = postToMerlinMutation.variables?.overrideRemittanceWait;

      // Server soft-gate: high-risk flagged lines need explicit acknowledgement.
      // This is the authoritative gate (the proactive client confirm in
      // handlePostToMerlin may have been skipped if the list data was stale).
      if (err?.requiresWarningAck && err?.paymentId) {
        if (confirmHighRiskWarnings(err.warningLines as WarningLine[] | undefined)) {
          postToMerlinMutation.mutate({
            paymentId: err.paymentId,
            acknowledgeWarnings: true,
            forceOnAccount: prevForce,
            overrideRemittanceWait: prevWait,
          });
        }
        return;
      }

      // Server soft-gate: the 48h remittance-reply window hasn't elapsed (or no
      // request was sent). Offer an explicit override so the operator can post
      // unallocated early — the override is audited server-side.
      if (err?.requiresWaitOverride && err?.paymentId) {
        const proceed = confirm(
          `${msg}\n\nClick OK to post unallocated ON-ACCOUNT now anyway, or Cancel to keep waiting for the customer's remittance reply.`,
        );
        if (proceed) {
          postToMerlinMutation.mutate({
            paymentId: err.paymentId,
            overrideRemittanceWait: true,
            forceOnAccount: prevForce,
            acknowledgeWarnings: prevAck,
          });
        }
        return;
      }

      const refusal = /Refusing to post on-account/i.test(msg);
      if (refusal && err?.paymentId) {
        const forced = confirm(
          `${msg}\n\nClick OK to post ON-ACCOUNT anyway (unallocated cash), or Cancel to leave the payment unposted so you can resolve the invoice allocation first.`,
        );
        if (forced) {
          postToMerlinMutation.mutate({
            paymentId: err.paymentId,
            forceOnAccount: true,
            acknowledgeWarnings: prevAck,
            overrideRemittanceWait: prevWait,
          });
          return;
        }
      }
      toast({ title: msg || "Failed to post to Merlin", variant: "destructive" });
    },
  });

  // Summarise high-risk flagged lines and require an explicit "post anyway"
  // confirmation. Returns true when the operator acknowledges. The
  // acknowledgement is recorded server-side in the audit log.
  const confirmHighRiskWarnings = (lines: WarningLine[] | undefined): boolean => {
    const highRisk = (lines ?? []).filter((l) => l.highRisk);
    if (highRisk.length === 0) return true;
    const summary = highRisk
      .map((l) => {
        const label = LINE_WARNING_META[l.warningCode]?.label ?? l.warningCode;
        const detail = l.warning ? `\n   ${l.warning}` : "";
        return `• ${l.reference} → ${l.account ?? "(no account)"}: ${label}${detail}`;
      })
      .join("\n");
    return confirm(
      `⚠ This remittance has ${highRisk.length} HIGH-RISK flagged line` +
        `${highRisk.length === 1 ? "" : "s"} that may post to the WRONG account:\n\n${summary}\n\n` +
        `Click OK to POST ANYWAY (your acknowledgement is recorded in the audit log), ` +
        `or Cancel to fix the flagged line(s) first.`,
    );
  };

  const handlePostToMerlin = (p: BankPayment) => {
    const onAccount = (p.status === "needs_review" || p.status === "awaiting_remittance")
      && (!p.allocatedInvoices || p.allocatedInvoices.length === 0);
    const what = onAccount
      ? `${fmtMoney(p.amount)} unallocated ON-ACCOUNT (transaction id -1; no invoice will be selected)`
      : p.status === "matched"
        ? "the matched remittance's invoices"
        : `${p.allocatedInvoices?.length ?? 0} invoice(s)`;
    const accountPhrase = p.resolvedAccount === MULTI_ACCOUNT_SENTINEL
      ? "across multiple accounts (each invoice booked to its own account)"
      : `under account ${p.resolvedAccount}`;
    if (!confirm(`Post CashReceipt to Merlin for ${what} ${accountPhrase}? This cannot be undone in Merlin.`)) return;

    // Pre-post soft gate: surface any flagged lines on the matched remittance.
    // Lower-risk codes are advisory (shown for context only); high-risk codes
    // require an explicit "post anyway" acknowledgement, which the server also
    // enforces and audits.
    const flagged = p.warningLines ?? [];
    const highRisk = flagged.filter((l) => l.highRisk);
    const advisory = flagged.filter((l) => !l.highRisk);
    if (advisory.length > 0 && highRisk.length === 0) {
      // Advisory-only: note them but don't block — they stay informational.
      const labels = advisory
        .map((l) => `${l.reference}: ${LINE_WARNING_META[l.warningCode]?.label ?? l.warningCode}`)
        .join("; ");
      toast({ title: `Posting with ${advisory.length} advisory warning(s)`, description: labels });
    }
    if (highRisk.length > 0) {
      if (!confirmHighRiskWarnings(highRisk)) return;
      postToMerlinMutation.mutate({ ...postToMerlinVariables(p), acknowledgeWarnings: true });
      return;
    }
    postToMerlinMutation.mutate(postToMerlinVariables(p));
  };

  const accountEditMutation = useMutation({
    mutationFn: async ({ id, account }: { id: number; account: string | null }) => {
      const res = await fetch(`${BASE}/api/cash-receipts/bank-payments/${id}/account`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({ account }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body as BankPayment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts", "payments"] });
      toast({ title: "Customer account updated" });
    },
    onError: (err: any) => toast({ title: err.message || "Failed to update account", variant: "destructive" }),
  });

  // Re-runs the reconciler against every needs_review row, plus any
  // unmatched/processing rows stranded by an earlier crash. Useful
  // after editing the ID rules so previously-stuck payments get a
  // second look — and as a manual recovery if the background
  // upload-time reconciler died mid-batch.
  const reReconcileMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/bank-payments/re-reconcile-needs-review`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body as {
        ok: boolean;
        reset: number;
        resetFromProcessing: number;
        attempted: number;
        outcomes: Record<string, number>;
        errors: any[];
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      if (data.attempted === 0 && data.reset === 0 && data.resetFromProcessing === 0) {
        toast({ title: "Nothing to re-reconcile", description: "All payments are already settled — no Needs Review or stranded rows found." });
        return;
      }
      const outcomes = data.outcomes || {};
      const parts: string[] = [];
      for (const [k, v] of Object.entries(outcomes)) parts.push(`${v} ${String(k).replace(/_/g, " ")}`);
      const extraBits: string[] = [];
      if (data.reset > 0) extraBits.push(`${data.reset} from Needs Review`);
      if (data.resetFromProcessing > 0) extraBits.push(`${data.resetFromProcessing} stuck-processing released`);
      const desc = [parts.join(", "), extraBits.join(" · ")].filter(Boolean).join(" — ");
      toast({
        title: `Re-reconciled ${data.attempted} payment(s)`,
        description: desc || undefined,
      });
    },
    onError: (err: any) => toast({ title: err.message || "Re-reconcile failed", variant: "destructive" }),
  });

  // Per-payment re-reconcile. Resets a stuck awaiting_remittance /
  // needs_review row and re-runs the reconciler against it — used to
  // re-try individual rows after an allocator/parser fix ships
  // (previously decided rows are otherwise terminal and would never
  // pick up the new logic on their own).
  const reReconcileOneMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/cash-receipts/bank-payments/${id}/re-reconcile`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body as {
        outcome: { kind: string; remittanceId?: number; [k: string]: any };
        remittanceMatch?: MatchResponse | null;
        payment: BankPayment | null;
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      if (data.remittanceMatch?.status === "matched") {
        toast({
          title: `Matched remittance #${data.outcome.remittanceId}`,
          description: `Linked to bank payment #${data.remittanceMatch.autoMatchedBankPaymentId}`,
        });
        return;
      }
      const kind = data.outcome?.kind ?? "unknown";
      toast({ title: `Re-reconciled`, description: `Outcome: ${kind.replace(/_/g, " ")}` });
    },
    onError: (err: any) => toast({ title: err.message || "Re-reconcile failed", variant: "destructive" }),
  });

  const recoverProcessingMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/cash-receipts/bank-payments/${id}/recover-processing`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
        signal: AbortSignal.timeout(90_000),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body as { released: boolean; outcome: { kind: string }; payment: BankPayment | null };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({ title: "Processing lock released", description: `Reconciliation result: ${result.outcome.kind.replace(/_/g, " ")}.` });
    },
    onError: (err: any) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({ title: "Check payment status", description: err.message || "Recovery failed", variant: "destructive" });
    },
  });

  // Super-admin only. Clears the merlin_posting_* / merlin_submissions
  // fields on a bank payment so the Post button is enabled again. The
  // backend refuses unless every recorded submission failed — i.e.
  // nothing actually landed in Merlin — so this is safe to expose as
  // a self-service button rather than a manual DB edit.
  const resetMerlinPostingMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/cash-receipts/bank-payments/${id}/reset-merlin-posting`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({ title: "Merlin posting state cleared", description: "The Post button is enabled again for this payment." });
    },
    onError: (err: any) => toast({ title: err.message || "Reset failed", variant: "destructive" }),
  });

  const handleResetMerlinPosting = (id: number) => {
    if (!confirm(
      `Clear the recorded Merlin posting state on bank payment ${id}?\n\n` +
      `This is only allowed when every recorded submission failed (nothing reached Merlin's sltran). ` +
      `After resetting, the Post button will be enabled again so you can retry.\n\n` +
      `Continue?`
    )) return;
    resetMerlinPostingMutation.mutate(id);
  };

  // Recover a crash-stuck PARTIAL post (the "investigate" state). Releases the
  // stale posting lease but PRESERVES the recorded submissions, so the resume
  // flow posts only the REMAINING invoices — already-posted ones are skipped.
  const releaseMerlinLeaseMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/cash-receipts/bank-payments/${id}/release-merlin-lease`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body;
    },
    onSuccess: (body: any) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({
        title: "Posting lock released",
        description:
          `${body?.postedSoFar ?? 0} invoice(s) already posted will be skipped. ` +
          `Use "Post remaining" to post the rest (or the auto-poster will pick it up shortly).`,
      });
    },
    onError: (err: any) => toast({ title: err.message || "Release failed", variant: "destructive" }),
  });

  const handleReleaseMerlinLease = (p: BankPayment) => {
    const subs = p.merlinSubmissions ?? [];
    const postedSoFar = subs.filter((s) => s.code === "0").length;
    if (!confirm(
      `Release the stuck Merlin posting lock on bank payment ${p.id} (${fmtMoney(p.amount)})?\n\n` +
      `This run crashed part-way through. ${postedSoFar} invoice(s) already reached Merlin and will NOT be re-posted; ` +
      `releasing the lock lets the remaining invoices post.\n\n` +
      `Only do this once you're confident the previous attempt is no longer running.\n\nContinue?`
    )) return;
    releaseMerlinLeaseMutation.mutate(p.id);
  };

  const handleReReconcile = () => {
    if (!confirm(
      `Re-run reconciliation against:\n` +
      `  • every payment currently in Needs Review (across ALL bank statements)\n` +
      `  • any Unmatched rows the background reconciler missed\n` +
      `  • side-effect-free rows stuck in Processing for more than 60 minutes\n\n` +
      `Any manually-set customer accounts on Needs Review rows will be cleared and re-evaluated against the current ID rules.\n\n` +
      `Continue?`
    )) return;
    reReconcileMutation.mutate();
  };

  // BACS-page-only exclusions: card-acquirer settlements and corporate-card
  // refunds are reconciled elsewhere, not here, so they would otherwise sit
  // as permanent noise in the "needs attention" list. Stripe rows are
  // typically narrated "STRIPE PAYMENTS UK LTD"; Barclaycard merchant
  // refunds start with "BCARD".
  const isReconciledElsewhere = (p: BankPayment) => {
    const n = (p.narrative ?? "").trim();
    if (/\bstripe\b/i.test(n)) return true;
    if (/^bcard\b/i.test(n)) return true;
    return false;
  };
  // A payment is "done" — i.e. it won't ever post to Merlin again — when
  // either Merlin has accepted it (merlinPostedAt set, regardless of any
  // partial-failure pill) or the operator has explicitly excluded/ignored
  // it. A day archives only when every payment on it is done.
  // Status filter is meaningful only on the Active tab — the Archived
  // tab is by construction all-posted/excluded, so any of the "hide
  // matched" / "unmatched only" presets would render every group empty.
  const effectiveStatusFilter = bacsSubTab === "archived" ? "all" : statusFilter;
  // hide_posted removes anything that no longer needs operator attention,
  // including rows Merlin reports as already settled. It still keeps matched /
  // auto_allocated / awaiting_review rows visible so the operator can post them.
  const rowMatchesStatus = (p: BankPayment) =>
    bankPaymentMatchesStatusFilter(p, effectiveStatusFilter);

  // Group all non-elsewhere-reconciled payments by paymentDate. The
  // archive decision uses the full group (not the filtered rows) so a
  // partially-posted day doesn't appear "fully posted" just because
  // the operator is hiding matched rows.
  const nonElsewherePayments = payments.filter((p) => !isReconciledElsewhere(p));
  const dateGroupsMap = new Map<string, BankPayment[]>();
  for (const p of nonElsewherePayments) {
    const key = p.paymentDate || "(undated)";
    const list = dateGroupsMap.get(key);
    if (list) list.push(p);
    else dateGroupsMap.set(key, [p]);
  }
  const allDateGroups = Array.from(dateGroupsMap.entries())
    .map(([date, ps]) => {
      const doneCount = ps.filter(isBankPaymentDone).length;
      return {
        date,
        payments: ps,
        doneCount,
        archived: ps.length > 0 && doneCount === ps.length,
        total: ps.reduce((s, x) => s + x.amount, 0),
      };
    })
    .sort((a, b) => b.date.localeCompare(a.date));

  const activeGroups = allDateGroups.filter((g) => !g.archived);
  const archivedGroups = allDateGroups.filter((g) => g.archived);
  const visibleGroups = bacsSubTab === "active" ? activeGroups : archivedGroups;
  const visibleRowCount = visibleGroups.reduce(
    (s, g) => s + g.payments.filter(rowMatchesStatus).length,
    0,
  );
  const totalUnmatched = payments.filter((p) => p.status === "unmatched" && !isReconciledElsewhere(p)).length;
  const hiddenElsewhereCount = payments.filter(isReconciledElsewhere).length;

  // Once payments load, reveal the deep-linked one: pick the right
  // active/archived sub-tab, expand its date group, then scroll to and
  // briefly highlight the row. Runs once per target id.
  useEffect(() => {
    if (focusPaymentId == null) return;
    if (focusHandledRef.current === focusPaymentId) return;
    const target = payments.find((p) => p.id === focusPaymentId);
    if (!target) return; // payments not loaded yet, or id not found
    focusHandledRef.current = focusPaymentId;
    setBacsSubTab(isBankPaymentDone(target) ? "archived" : "active");
    const key = target.paymentDate || "(undated)";
    setExpandedDates((prev) => new Set(prev).add(key));
    const t = setTimeout(() => {
      const el = document.getElementById(`payment-${focusPaymentId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-primary", "ring-inset", "bg-primary/5");
      setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-inset", "bg-primary/5"), 3000);
    }, 250);
    return () => clearTimeout(t);
  }, [focusPaymentId, payments]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-duffells-grey flex items-center gap-2">
              <Banknote className="w-6 h-6 text-primary" />
              Cash Receipts
            </h1>
            <p className="text-sm text-duffells-grey/60 mt-1">
              Match incoming bank payments against customer remittances to confirm which invoices are paid.
            </p>
          </div>
          {isSuperAdmin && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={testMerlinConnection}
              disabled={pingingMerlin}
              title="Calls Merlin's SOAP CheckStatus through QuotaGuard — same transport as CashReceipt, no money posted."
            >
              {pingingMerlin ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Plug className="w-4 h-4 mr-1.5" />
              )}
              Test Merlin connection
            </Button>
          )}
        </div>

        <div className="flex gap-1 border-b border-slate-200">
          <button
            onClick={() => setTab("bacs")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === "bacs" ? "border-primary text-primary" : "border-transparent text-duffells-grey/60 hover:text-duffells-grey"
            }`}
          >
            <Receipt className="w-4 h-4 inline mr-1" /> BACS
          </button>
          <button
            onClick={() => setTab("remittances")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === "remittances" ? "border-primary text-primary" : "border-transparent text-duffells-grey/60 hover:text-duffells-grey"
            }`}
          >
            <FileText className="w-4 h-4 inline mr-1" /> Remittances
          </button>
          <button
            onClick={() => setTab("stripe")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === "stripe" ? "border-primary text-primary" : "border-transparent text-duffells-grey/60 hover:text-duffells-grey"
            }`}
          >
            <CreditCard className="w-4 h-4 inline mr-1" /> Stripe
          </button>
          <button
            onClick={() => setTab("credit")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === "credit" ? "border-primary text-primary" : "border-transparent text-duffells-grey/60 hover:text-duffells-grey"
            }`}
          >
            <ArrowLeftRight className="w-4 h-4 inline mr-1" /> Credit matching
          </button>
          <button
            onClick={() => setTab("unallocated")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === "unallocated" ? "border-primary text-primary" : "border-transparent text-duffells-grey/60 hover:text-duffells-grey"
            }`}
          >
            <Wallet className="w-4 h-4 inline mr-1" /> Unallocated payment matching
          </button>
        </div>

        {tab === "remittances" && (
          <RemittancesTab
            focusRemittanceId={resolveRemittanceId}
            onFocusHandled={() => setResolveRemittanceId(null)}
          />
        )}

        {tab === "stripe" && <StripeTab isAdmin={isRealAdmin || isSuperAdmin} />}

        {tab === "credit" && <CreditMatchingTab />}

        {tab === "unallocated" && <UnallocatedMatchingTab />}

        {tab === "bacs" && (
          <>
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="font-semibold text-duffells-grey mb-3 flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload Daily Bank Statement
              </h2>
              <p className="text-xs text-duffells-grey/60 mb-3">
                Both accounts remain available. Choose the bank account before uploading; the saved account determines the Merlin nominal.
              </p>
              <label className="mb-3 block text-sm text-duffells-grey">
                Bank account
                <select aria-label="Bank account" value={bankSource} disabled={uploading}
                  onChange={(e) => setBankSource(e.target.value as typeof bankSource)}
                  className="mt-1 block w-full max-w-lg rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <option value="barclays_dcs">Existing account — Sales Finance CSV — nominal 17012</option>
                  <option value="barclays_current">Current account — Barclays XLSX — nominal 17001</option>
                </select>
              </label>
              <FileDropzone
                onUpload={handleFiles}
                accept={bankSource === "barclays_current" ? ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : ".csv,text/csv"}
                label={bankSource === "barclays_current" ? "Drop Current account XLSX here" : "Drop Daily Client Summary CSV here"}
                multiple={true}
                maxSize={10 * 1024 * 1024}
              />
              {uploading && (
                <div className="mt-3 flex items-center gap-2 text-sm text-duffells-grey">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Parsing bank statement…
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-duffells-grey">Uploaded bank statements</h2>
                  <p className="text-xs text-duffells-grey/60 mt-0.5">
                    Statements auto-archive once they're over 14 days old and every payment is posted, excluded or ignored. You can also archive or restore any statement yourself.
                  </p>
                </div>
                {filterUploadId && (
                  <button onClick={() => setFilterUploadId(null)} className="text-xs text-primary hover:underline">
                    Clear filter
                  </button>
                )}
              </div>
              <div className="px-6 pt-3 flex items-center gap-4 border-b border-slate-100">
                <button
                  onClick={() => setUploadsSubTab("active")}
                  className={`pb-2 text-sm font-medium border-b-2 -mb-px ${
                    uploadsSubTab === "active" ? "border-primary text-primary" : "border-transparent text-duffells-grey/60 hover:text-duffells-grey"
                  }`}
                >
                  Active
                  <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${uploadsSubTab === "active" ? "bg-primary/10 text-primary" : "bg-slate-200 text-slate-600"}`}>
                    {activeUploads.length}
                  </span>
                </button>
                <button
                  onClick={() => setUploadsSubTab("archived")}
                  className={`pb-2 text-sm font-medium border-b-2 -mb-px ${
                    uploadsSubTab === "archived" ? "border-primary text-primary" : "border-transparent text-duffells-grey/60 hover:text-duffells-grey"
                  }`}
                >
                  Archived
                  <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${uploadsSubTab === "archived" ? "bg-primary/10 text-primary" : "bg-slate-200 text-slate-600"}`}>
                    {archivedUploads.length}
                  </span>
                </button>
              </div>
              {uploadsLoading ? (
                <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
              ) : visibleUploads.length === 0 ? (
                <div className="p-8 text-center text-duffells-grey/60 text-sm">
                  {uploadsSubTab === "archived" ? "No archived statements." : "No bank statements uploaded yet."}
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/70">
                    <tr>
                      <th className="px-6 py-3 text-left">Filename</th>
                      <th className="px-6 py-3 text-left">Statement date</th>
                      <th className="px-6 py-3 text-right">Payments</th>
                      <th className="px-6 py-3 text-right">Total received</th>
                      <th className="px-6 py-3 text-left">Uploaded</th>
                      <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {visibleUploads.map((u) => (
                      <tr
                        key={u.id}
                        onClick={() => setFilterUploadId(u.id === filterUploadId ? null : u.id)}
                        className={`cursor-pointer hover:bg-slate-50 ${filterUploadId === u.id ? "bg-primary/5" : ""}`}
                      >
                        <td className="px-6 py-3 text-sm font-medium text-duffells-grey truncate max-w-md">
                          {u.originalFilename}
                          <div className="mt-1 text-xs font-normal text-duffells-grey/60">
                            {u.accountLabel}{u.nominal != null && ` · Merlin nominal ${u.nominal}`}
                          </div>
                          {u.archivedAt && (
                            <span className="ml-2 text-[10px] font-normal px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600 align-middle">
                              {u.archivedReason === "auto_age" ? "Auto-archived" : "Archived"}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-sm text-duffells-grey">{fmtDate(u.statementDate)}</td>
                        <td className="px-6 py-3 text-sm text-duffells-grey text-right">{u.paymentCount}</td>
                        <td className="px-6 py-3 text-sm text-duffells-grey text-right font-medium">{fmtMoney(u.totalReceived)}</td>
                        <td className="px-6 py-3 text-xs text-duffells-grey/60">{fmtDate(u.createdAt)}</td>
                        <td className="px-6 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); archiveUploadMutation.mutate({ id: u.id, archived: !u.archivedAt }); }}
                              disabled={archiveUploadMutation.isPending}
                              className="text-duffells-grey/60 hover:text-primary disabled:opacity-50"
                              title={u.archivedAt ? "Restore to Active" : "Archive this statement"}
                            >
                              {u.archivedAt ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDelete(u); }}
                              className="text-red-500 hover:text-red-700"
                              title="Delete this statement and all its payments"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-duffells-grey">
                    Bank payments {filterUploadId && uploads.find((u) => u.id === filterUploadId) && (
                      <span className="text-sm font-normal text-duffells-grey/60">
                        — {uploads.find((u) => u.id === filterUploadId)!.originalFilename}
                      </span>
                    )}
                  </h2>
                  <p className="text-xs text-duffells-grey/60 mt-0.5">
                    {visibleGroups.length} {visibleGroups.length === 1 ? "day" : "days"} &middot; {visibleRowCount} shown &middot; {payments.length} total &middot; {totalUnmatched} unmatched
                    {hiddenElsewhereCount > 0 && (
                      <> &middot; {hiddenElsewhereCount} hidden (Stripe / BCARD)</>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleReReconcile}
                    disabled={reReconcileMutation.isPending}
                    className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-duffells-grey hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1.5"
                    title="Re-run the reconciler against every Needs Review row (across all bank statements), plus any Unmatched/Processing rows stranded by a previous crash. Useful after editing ID rules or to recover from background-job failures."
                  >
                    {reReconcileMutation.isPending
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <RefreshCw className="w-3.5 h-3.5" />}
                    Re-run reconciliation
                  </button>
                  <label className="text-xs text-duffells-grey/60">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    disabled={bacsSubTab === "archived"}
                    title={bacsSubTab === "archived" ? "Status filter is ignored on Archived — every payment is already posted/excluded." : undefined}
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1 bg-white text-duffells-grey focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="hide_posted">Hide posted to Merlin (default)</option>
                    <option value="hide_matched">Hide matched</option>
                    <option value="all">All</option>
                    <option value="unmatched">Unmatched only</option>
                    <option value="matched">Matched only</option>
                    <option value="auto_allocated">Auto-allocated only</option>
                    <option value="awaiting_remittance">Awaiting remittance</option>
                    <option value="awaiting_review">Awaiting review (approval required)</option>
                    <option value="needs_review">Needs review</option>
                    <option value="processing">Processing</option>
                    <option value="excluded">Excluded (by rule)</option>
                    <option value="ignored">Ignored</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-1 px-6 pt-3 border-b border-slate-100 bg-slate-50/50">
                <button
                  onClick={() => setBacsSubTab("active")}
                  className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 ${
                    bacsSubTab === "active" ? "border-primary text-primary" : "border-transparent text-duffells-grey/60 hover:text-duffells-grey"
                  }`}
                >
                  Active
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${bacsSubTab === "active" ? "bg-primary/10 text-primary" : "bg-slate-200 text-slate-600"}`}>
                    {activeGroups.length}
                  </span>
                </button>
                <button
                  onClick={() => setBacsSubTab("archived")}
                  className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 ${
                    bacsSubTab === "archived" ? "border-primary text-primary" : "border-transparent text-duffells-grey/60 hover:text-duffells-grey"
                  }`}
                  title="Days where every payment has been posted to Merlin (or excluded/ignored)."
                >
                  Archived
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${bacsSubTab === "archived" ? "bg-primary/10 text-primary" : "bg-slate-200 text-slate-600"}`}>
                    {archivedGroups.length}
                  </span>
                </button>
              </div>
              {paymentsLoading ? (
                <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
              ) : payments.length === 0 ? (
                <div className="p-8 text-center text-duffells-grey/60 text-sm">No payments — upload a bank statement above.</div>
              ) : visibleGroups.length === 0 ? (
                <div className="p-8 text-center text-duffells-grey/60 text-sm">
                  {bacsSubTab === "archived"
                    ? "No days fully posted to Merlin yet."
                    : "No active days — every uploaded day is fully posted. Switch to Archived to review them."}
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {visibleGroups.map((g) => {
                    const isOpen = expandedDates.has(g.date);
                    const rowsInGroup = g.payments.filter(rowMatchesStatus);
                    const undatedLabel = g.date === "(undated)" ? "(no date)" : fmtDate(g.date);
                    return (
                  <div key={g.date}>
                    <button
                      type="button"
                      onClick={() => toggleDate(g.date)}
                      className="w-full px-6 py-3 flex items-center justify-between hover:bg-slate-50 text-left"
                      aria-expanded={isOpen}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {isOpen
                          ? <ChevronDown className="w-4 h-4 text-duffells-grey/60 flex-shrink-0" />
                          : <ChevronRight className="w-4 h-4 text-duffells-grey/60 flex-shrink-0" />}
                        <span className="font-medium text-duffells-grey whitespace-nowrap">{undatedLabel}</span>
                        <span className="text-xs text-duffells-grey/60 whitespace-nowrap">
                          {g.payments.length} payment{g.payments.length === 1 ? "" : "s"}
                          {g.archived
                            ? " · all posted"
                            : ` · ${g.doneCount}/${g.payments.length} posted`}
                          {effectiveStatusFilter !== "all" && rowsInGroup.length !== g.payments.length && (
                            <> · {rowsInGroup.length} match filter</>
                          )}
                        </span>
                        {g.archived && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">
                            archived
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-medium text-duffells-grey whitespace-nowrap ml-4">{fmtMoney(g.total)}</span>
                    </button>
                    {isOpen && (
                      rowsInGroup.length === 0 ? (
                        <div className="px-6 py-3 text-xs text-duffells-grey/60 italic bg-slate-50/40">
                          No payments in this day match the current filter.{" "}
                          <button onClick={() => setStatusFilter("all")} className="text-primary hover:underline not-italic">Show all</button>
                        </div>
                      ) : (
                <table className="w-full">
                  <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/70">
                    <tr>
                      <th className="px-6 py-3 text-right">Amount</th>
                      <th className="px-6 py-3 text-left">Narrative</th>
                      <th className="px-6 py-3 text-left">Detected hints</th>
                      <th className="px-6 py-3 text-left">Status</th>
                      <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rowsInGroup.map((p) => {
                      const hasAllocatedInvoices = !!p.allocatedInvoices && p.allocatedInvoices.length > 0;
                      // A row is "invoice-postable" when the server-side targetsForBankPayment
                      // will return non-empty targets:
                      //   - auto_allocated / awaiting_review: targets come from allocatedInvoices
                      //     (BACS-narrative reconciler / LLM reply-parser)
                      //   - matched: targets come from the LINKED REMITTANCE'S LINES, not
                      //     allocatedInvoices (which stays NULL for matched rows). Gate on
                      //     matchedRemittanceId, not hasAllocatedInvoices, or the Post button
                      //     never appears for remittance-matched rows.
                      const hasMatchedRemittance = p.status === "matched" && p.matchedRemittanceId != null;
                      const subs = p.merlinSubmissions ?? [];
                      const latestSubmissions = latestMerlinSubmissions(subs);
                      const anyFailed = latestSubmissions.some((s) => s.code !== "0");
                      const hasAmbiguousFailure = latestSubmissions.some(
                        (s) => s.code !== "0" && !/^\d+$/.test(s.code),
                      );
                      const hasPostedLines = latestSubmissions.some((s) => s.code === "0");
                      const terminalPartial = !!p.merlinPostedAt && hasPostedLines && anyFailed && !hasAmbiguousFailure;
                      const postingAvailable = terminalPartial || (!p.merlinPostingStartedAt && !p.merlinPostedAt);
                      const isInvoicePostable = ((
                        (p.status === "auto_allocated" || p.status === "awaiting_review") && hasAllocatedInvoices
                      ) || hasMatchedRemittance)
                        && postingAvailable && !!p.resolvedAccount;
                      const isOnAccountPostable = (p.status === "needs_review" || p.status === "awaiting_remittance")
                        && !p.merlinPostingStartedAt && !p.merlinPostedAt && !!p.resolvedAccount && !hasAllocatedInvoices;
                      const isPostable = isInvoicePostable || isOnAccountPostable;
                      // On-account posting waits for the customer: a remittance must
                      // have been requested and 48h must have passed with no reply.
                      // Until then the primary Post button is disabled (with an
                      // explanation) but an explicit override is offered.
                      const onAccountWait = isOnAccountPostable ? p.onAccountWait : null;
                      const onAccountWaitBlocked = !!onAccountWait?.blocked;
                      // A matched multi-account remittance with line(s) that have no
                      // resolved Merlin account cannot be posted (the server rejects it).
                      // The button stays visible but is disabled with an explanatory tooltip.
                      const postBlockedReason = p.postBlockedReason;
                      // Flagged lines on the matched remittance. High-risk ones
                      // soft-gate posting (require "post anyway"); the rest are
                      // advisory. Surface them inline next to the Post button so
                      // operators see *which* lines are flagged before clicking,
                      // mirroring the always-visible postBlockedReason text.
                      const flaggedLines = p.warningLines ?? [];
                      const highRiskLines = flaggedLines.filter((l) => l.highRisk);
                      const advisoryLines = flaggedLines.filter((l) => !l.highRisk);
                      const isPosting = postToMerlinMutation.isPending && postToMerlinMutation.variables?.paymentId === p.id;
                       const incomplete = !!p.merlinPostingStartedAt && !p.merlinPostedAt;
                      // A partial post released its lease (startedAt + postedAt both
                      // null) but already landed some lines in Merlin (code-0 subs).
                      // The remaining review lines still need resolving, so the Post
                      // button stays available — relabelled "Post remaining".
                       const isPartiallyPosted =
                         hasPostedLines && (terminalPartial || (!p.merlinPostedAt && !p.merlinPostingStartedAt));
                      // Once a payment begins posting to Merlin its customer
                      // account is baked into what reached Merlin, so the field
                      // is shown read-only with a lock affordance + explanation
                      // (mirrors the per-line LineAccountCell treatment) instead
                      // of an editor that would 409 server-side.
                      const isSavingAccount = accountEditMutation.isPending
                        && accountEditMutation.variables?.id === p.id;
                      const bankUpload = uploads.find((u) => u.id === p.uploadId);
                      return (
                      <tr key={p.id} id={`payment-${p.id}`} className="hover:bg-slate-50 transition-colors rounded">
                        <td className="px-6 py-3 text-sm text-duffells-grey text-right font-medium whitespace-nowrap">{fmtMoney(p.amount)}</td>
                        <td className="px-6 py-3 text-sm text-duffells-grey/80 max-w-md">
                          <span className="font-mono text-xs">{p.narrative || <span className="text-slate-400">(empty)</span>}</span>
                          {bankUpload && <div className="mt-1 text-[11px] text-duffells-grey/60">
                            {bankUpload.accountLabel}{bankUpload.nominal != null && ` · nominal ${bankUpload.nominal}`}
                          </div>}
                        </td>
                        <td className="px-6 py-3 text-xs space-y-0.5">
                          {p.customerHintInvoice && <div><span className="text-duffells-grey/50">Invoice:</span> <span className="font-mono text-duffells-grey">{p.customerHintInvoice}</span></div>}
                          {p.customerHintAccount && <div><span className="text-duffells-grey/50">Account:</span> <span className="font-mono text-duffells-grey">{p.customerHintAccount}</span></div>}
                          {p.customerHintEftRef && <div><span className="text-duffells-grey/50">EFT:</span> <span className="font-mono text-duffells-grey">{p.customerHintEftRef}</span></div>}
                          {!p.customerHintInvoice && !p.customerHintAccount && !p.customerHintEftRef && <span className="text-slate-400">none</span>}
                          <div className="pt-1">
                            <PaymentAccountCell
                              payment={p}
                              disabled={isSavingAccount}
                              onSave={(account) => accountEditMutation.mutate({ id: p.id, account })}
                            />
                          </div>
                          {hasAllocatedInvoices && (
                            <div>
                              <span className="text-duffells-grey/50">→ invs:</span>{" "}
                              <span className="font-mono text-emerald-700">
                                {p.allocatedInvoices!.map((a) => `${a.ref} £${a.balance.toFixed(2)}`).join(", ")}
                              </span>
                              {p.replyAllocationSource && (
                                <span
                                  className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-medium"
                                  title={p.replyParseNotes || "Auto-matched from customer email reply"}
                                >
                                  auto-matched ({p.replyAllocationSource})
                                </span>
                              )}
                            </div>
                          )}
                          {p.replyReceivedAt && p.replyText && (
                            <details className="pt-1">
                              <summary className="cursor-pointer text-[11px] text-violet-700 hover:underline select-none">
                                Reply received {fmtDate(p.replyReceivedAt)}
                                {p.replyAllocationSource === "split" && (
                                  <span className="ml-1 text-amber-700">(split — review carefully)</span>
                                )}
                              </summary>
                              <div className="mt-1 p-2 bg-violet-50 border border-violet-100 rounded text-[11px] text-duffells-grey/80 whitespace-pre-wrap font-sans max-h-40 overflow-y-auto">
                                {p.replyText}
                              </div>
                              {p.replyParseNotes && (
                                <div className="mt-1 text-[10px] text-duffells-grey/60 italic">{p.replyParseNotes}</div>
                              )}
                            </details>
                          )}
                        </td>
                        <td className="px-6 py-3 space-y-1">
                          {statusPill(p.status)}
                          {p.status === "processing" && p.updatedAt
                            && Date.now() - new Date(p.updatedAt).getTime() >= 60 * 60 * 1000
                            && !p.merlinPostingStartedAt && !p.merlinPostedAt
                            && !p.merlinSubmissions?.length
                            && !p.allocatedInvoices?.length
                            && !p.remittanceRequestedAt && !p.replyToken && !p.matchedRemittanceId && (
                            <button
                              className="block text-[11px] text-amber-700 hover:underline disabled:opacity-50"
                              disabled={recoverProcessingMutation.isPending}
                              onClick={() => {
                                if (!confirm(`Retry stalled reconciliation for payment #${p.id} (${fmtMoney(p.amount)})?\n\nOnly this payment will be released. The server will refuse if it has any Merlin posting, email-request intent, remittance link, or proforma action. This does not post cash to Merlin by itself.`)) return;
                                recoverProcessingMutation.mutate(p.id);
                              }}
                            >
                              {recoverProcessingMutation.isPending && recoverProcessingMutation.variables === p.id
                                ? "Recovering…"
                                : "Retry stalled reconciliation"}
                            </button>
                          )}
                          {(p.status === "awaiting_remittance" || p.status === "needs_review" || p.status === "awaiting_review") && !p.merlinPostingStartedAt && (
                            <div>
                              <button
                                onClick={() => {
                                  if (!confirm(`Re-run the reconciler against payment #${p.id} (${fmtMoney(p.amount)})?\n\nThis wipes the current resolved account, allocations, and remittance-request state, then re-evaluates from scratch. If no exact invoice match is found, the customer WILL be emailed again asking for a remittance advice.\n\nUse this after shipping an allocator/parser fix to re-try a stuck row.`)) return;
                                  reReconcileOneMutation.mutate(p.id);
                                }}
                                disabled={reReconcileOneMutation.isPending && reReconcileOneMutation.variables === p.id}
                                className="block text-[11px] text-slate-500 hover:text-slate-800 underline disabled:opacity-50"
                                title="Reset this row to Unmatched and re-run the reconciler. Useful after a fix that should now produce a match. Preserves any customer email reply already received."
                              >
                                {reReconcileOneMutation.isPending && reReconcileOneMutation.variables === p.id
                                  ? "Re-reconciling…"
                                  : "Re-reconcile"}
                              </button>
                            </div>
                          )}
                          {incomplete && (
                            <div>
                              <span
                                className="text-xs px-2 py-1 rounded-full font-medium inline-flex items-center gap-1 bg-amber-100 text-amber-800"
                                title={
                                  subs.length === 0
                                    ? "Posting was claimed but no invoices have been recorded yet. Investigate before retrying — the run may have crashed."
                                    : subs.map(fmtSubmission).join("\n")
                                }
                              >
                                <AlertCircle className="w-3 h-3" />
                                Merlin: investigate ({subs.length} recorded)
                              </span>
                              {isRealAdmin && (
                                <button
                                  onClick={() => handleReleaseMerlinLease(p)}
                                  disabled={releaseMerlinLeaseMutation.isPending && releaseMerlinLeaseMutation.variables === p.id}
                                  className="block mt-1 text-[11px] text-amber-700 hover:text-amber-900 underline disabled:opacity-50"
                                  title="Release a crash-stuck posting lock and let the remaining invoices post. Already-posted invoices are skipped — they are not re-posted. Allowed once the lock is over 5 minutes old. Admin only."
                                >
                                  {releaseMerlinLeaseMutation.isPending && releaseMerlinLeaseMutation.variables === p.id
                                    ? "Releasing…"
                                    : "Release lock & resume"}
                                </button>
                              )}
                            </div>
                          )}
                          {!incomplete && p.merlinPostedAt && (
                            <div className="space-y-1">
                              <span
                                className={`text-xs px-2 py-1 rounded-full font-medium inline-flex items-center gap-1 ${
                                  anyFailed ? "bg-red-100 text-red-700" : "bg-indigo-100 text-indigo-700"
                                }`}
                                title={subs.map(fmtSubmission).join("\n")}
                              >
                                {anyFailed ? <AlertCircle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                                {anyFailed ? "Merlin: partial" : "Posted to Merlin"}
                              </span>
                              {isRealAdmin && p.merlinPostingStartedAt && subs.length > 0 && subs.every((s) => s.code !== "0") && (
                                <button
                                  onClick={() => handleResetMerlinPosting(p.id)}
                                  disabled={resetMerlinPostingMutation.isPending && resetMerlinPostingMutation.variables === p.id}
                                  className="block text-[11px] text-slate-500 hover:text-slate-800 underline disabled:opacity-50"
                                  title="Clear the recorded posting state so the Post button works again. Allowed only when every recorded submission failed (nothing reached Merlin's sltran). Admin only."
                                >
                                  {resetMerlinPostingMutation.isPending && resetMerlinPostingMutation.variables === p.id
                                    ? "Resetting…"
                                    : "Reset posting state"}
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-3 text-right">
                          {isPostable && (
                            // A disabled Button has `disabled:pointer-events-none`, so its own
                            // title tooltip never fires. Wrap it in a non-disabled span that
                            // carries the title (hover/focus works), and render the reason as
                            // always-visible inline text so the explanation does not depend on
                            // a hover at all.
                            <div className="inline-flex flex-col items-end gap-1">
                              <span title={postBlockedReason ?? (onAccountWaitBlocked ? onAccountWait?.reason ?? undefined : undefined)} className="inline-flex">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={isPosting || !!postBlockedReason || onAccountWaitBlocked}
                                  onClick={() => handlePostToMerlin(p)}
                                  title={
                                    postBlockedReason || onAccountWaitBlocked
                                      ? undefined
                                      : isOnAccountPostable
                                        ? "Post as on-account (transaction id -1) — no invoice will be allocated in Merlin"
                                        : undefined
                                  }
                                >
                                  {isPosting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <PlayCircle className="w-3 h-3 mr-1" />}
                                  {isPartiallyPosted
                                    ? "Post remaining"
                                    : isOnAccountPostable
                                      ? "Post on-account"
                                      : "Mark as Paid in Merlin"}
                                </Button>
                              </span>
                              {postBlockedReason && (
                                <span className="flex items-start gap-1 text-[11px] text-amber-700 max-w-[14rem] text-right">
                                  <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                                  <span>{postBlockedReason}</span>
                                </span>
                              )}
                              {onAccountWaitBlocked && (
                                <>
                                  <span className="flex items-start gap-1 text-[11px] text-amber-700 max-w-[16rem] text-right">
                                    <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                                    <span>{onAccountWait?.reason}</span>
                                  </span>
                                  <button
                                    onClick={() => {
                                      if (
                                        confirm(
                                          `Post ${fmtMoney(p.amount)} unallocated ON-ACCOUNT now, before the customer's remittance reply? This is an early override and will be recorded in the audit log.`,
                                        )
                                      ) {
                                        postToMerlinMutation.mutate({
                                          ...postToMerlinVariables(p),
                                          overrideRemittanceWait: true,
                                        });
                                      }
                                    }}
                                    disabled={isPosting}
                                    className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:underline disabled:opacity-50"
                                    title="Override the 48h remittance wait and post unallocated now. Audited."
                                  >
                                    Post unallocated anyway <ArrowRight className="w-3 h-3" />
                                  </button>
                                </>
                              )}
                              {postBlockedReason && p.matchedRemittanceId != null && (
                                <button
                                  onClick={() => handleResolveLines(p.matchedRemittanceId as number)}
                                  className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                                  title="Open the matched remittance so you can resolve the unresolved account(s) here, then come back to post."
                                >
                                  Resolve lines <ArrowRight className="w-3 h-3" />
                                </button>
                              )}
                              {highRiskLines.length > 0 && (
                                <span className="flex items-start gap-1 text-[11px] text-amber-700 max-w-[16rem] text-right">
                                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                                  <span>
                                    {highRiskLines.length} high-risk flagged line
                                    {highRiskLines.length === 1 ? "" : "s"} — posting needs acknowledgement:{" "}
                                    {highRiskLines
                                      .map((l) => `${l.reference} (${LINE_WARNING_META[l.warningCode]?.label ?? l.warningCode})`)
                                      .join(", ")}
                                  </span>
                                </span>
                              )}
                              {advisoryLines.length > 0 && highRiskLines.length === 0 && (
                                <span className="flex items-start gap-1 text-[11px] text-slate-500 max-w-[16rem] text-right">
                                  <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                                  <span>
                                    {advisoryLines.length} advisory warning
                                    {advisoryLines.length === 1 ? "" : "s"}:{" "}
                                    {advisoryLines
                                      .map((l) => `${l.reference} (${LINE_WARNING_META[l.warningCode]?.label ?? l.warningCode})`)
                                      .join(", ")}
                                  </span>
                                </span>
                              )}
                              {!postBlockedReason && flaggedLines.length > 0 && p.matchedRemittanceId != null && (
                                <button
                                  onClick={() => handleResolveLines(p.matchedRemittanceId as number)}
                                  className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                                  title="Open the matched remittance so you can review and fix the flagged line(s) here, then come back to post."
                                >
                                  Resolve lines <ArrowRight className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                      )
                    )}
                  </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

interface Remittance {
  id: number;
  originalFilename: string;
  sourceFormat: string;
  customerName: string | null;
  customerAccount: string | null;
  remittanceDate: string | null;
  totalAmount: number | null;
  handwrittenPayAmount: number | null;
  lineCount: number;
  status: string;
  parseNotes: string | null;
  resolvedAccount: string | null;
  matchAttemptedAt: string | null;
  matchNotes: string | null;
  matchedBankPaymentId: number | null;
  archivedAt: string | null;
  archivedReason: string | null;
  merlinSettledAmount: number | null;
  merlinSettledCount: number | null;
  merlinPostingConfidence: string | null;
  createdAt: string;
}

interface SuggestedAllocationResponse {
  available: boolean;
  reason?: string;
  account?: string;
  amount?: number;
  allocation?: {
    amount: number;
    lines: Array<{ ref: string; balance: number; applied: number }>;
    allocatedTotal: number;
    remainder: number;
    fullyAllocated: boolean;
  };
  // Every outstanding (positive-balance) invoice on the account, oldest-first —
  // a superset of allocation.lines so the operator can add one the suggestion
  // didn't reach.
  outstanding?: Array<{ ref: string; balance: number }>;
}

interface RemittanceLine {
  id: number;
  reference: string;
  inferredReference: string | null;
  referenceType: string;
  amount: number;
  documentDate: string | null;
  rawLine: string;
  merlinAccount: string | null;
  merlinInvoiceTotal: number | null;
  merlinPaymentAmount: number | null;
  merlinCheckedAt: string | null;
  merlinNotFound: number | null;
  merlinAccountWarningCode: string | null;
  merlinAccountWarning: string | null;
  matchedBankPaymentId: number | null;
  accountSetBy: string | null;
  accountSetByName: string | null;
  accountSetAt: string | null;
}

interface BankCandidate {
  bankPaymentId: number;
  uploadId: number;
  paymentDate: string;
  amount: number;
  narrative: string;
  customerHintAccount: string | null;
  customerHintInvoice: string | null;
  customerHintEftRef: string | null;
  score: number;
  reasons: string[];
}

interface MatchResponse {
  remittanceId: number;
  resolvedAccount: string | null;
  status: string;
  notes: string[];
  candidates: BankCandidate[];
  autoMatchedBankPaymentId: number | null;
  archived: {
    settledCount: number;
    totalLines: number;
    settledAmount: number;
    remittanceTotal: number | null;
    confidence: string;
  } | null;
}

interface RemittanceDetail extends Remittance {
  lines: RemittanceLine[];
  rawText?: string;
  // Set when the matched bank payment has begun posting to Merlin. While this
  // is set, the per-line account PATCH route rejects edits (409), so the UI
  // locks the line account fields rather than letting an edit fail.
  matchedPaymentPostingStartedAt?: string | null;
}

function RemittancesTab({
  focusRemittanceId = null,
  onFocusHandled,
}: {
  // Set when the operator clicked "Resolve lines" on a blocked BACS row. We
  // expand, scroll to and highlight this remittance, then call onFocusHandled
  // so the same target can be re-triggered on a later click.
  focusRemittanceId?: number | null;
  onFocusHandled?: () => void;
} = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [batchMatching, setBatchMatching] = useState(false);

  // Filter & sort state
  const [filterAccount, setFilterAccount] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterAmountMin, setFilterAmountMin] = useState("");
  const [filterAmountMax, setFilterAmountMax] = useState("");
  const [sortField, setSortField] = useState<"date" | "account" | "amount">("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [filterOpen, setFilterOpen] = useState(false);

  const hasActiveFilter = !!(filterAccount || filterDateFrom || filterDateTo || filterAmountMin || filterAmountMax);

  const clearFilters = () => {
    setFilterAccount("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterAmountMin("");
    setFilterAmountMax("");
  };

  const { data: remittances = [], isLoading, isError, error } = useQuery<Remittance[]>({
    queryKey: ["cash-receipts", "remittances"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load remittances (HTTP ${res.status})`);
      }
      return res.json();
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const post = async (force: boolean) => {
        const fd = new FormData();
        fd.append("file", file);
        const url = `${BASE}/api/cash-receipts/remittances/upload${force ? "?force=1" : ""}`;
        return fetch(url, {
          method: "POST",
          body: fd,
          credentials: "include",
          headers: { "X-Requested-With": "fetch" },
        });
      };
      let res = await post(false);
      if (res.status === 409) {
        // The 409 body can only be read once — capture it here and reuse it
        // for both the soft-duplicate confirm flow AND the error message,
        // otherwise a second res.json() throws on the consumed stream and we
        // lose the server's message (e.g. the hard "already uploaded" one).
        const body = await res.json().catch(() => ({} as any));
        if (body.potentialDuplicate && Array.isArray(body.existing) && body.existing.length > 0) {
          // Soft duplicate (same total + same date as an existing remittance).
          // Show the operator what we already have and let them decide.
          const lines = body.existing.slice(0, 5).map((d: any) => {
            const who = d.resolvedAccount || d.customerAccount || d.customerName || d.originalFilename || "—";
            return `  #${d.id}  ${who}  £${d.totalAmount}  ${d.remittanceDate}  [${d.status}]`;
          }).join("\n");
          const summary = body.parsed
            ? `\n\nThis upload: £${body.parsed.totalAmount} on ${body.parsed.remittanceDate} (${body.parsed.lineCount} line(s))`
            : "";
          const ok = confirm(
            `Looks like this remittance has already been uploaded.\n\nMatching existing remittance(s):\n${lines}${summary}\n\nUpload anyway?`,
          );
          if (!ok) throw new Error(`Skipped (duplicate of #${body.existing[0].id})`);
          res = await post(true);
        } else {
          // Hard duplicate (same file bytes) or any other 409 — surface the
          // server's message directly from the body we already parsed.
          throw new Error(body.error || "This remittance has already been uploaded");
        }
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Upload failed");
      }
      return res.json();
    },
    onSuccess: (data: Remittance) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts", "remittances"] });
      const who = data.customerName || data.customerAccount || data.originalFilename;
      toast({ title: `Parsed remittance from ${who}: ${data.lineCount} line(s)` });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({ title: "Remittance deleted" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const matchAll = async () => {
    const pending = remittances.filter((r) => r.status === "unmatched" && !r.matchAttemptedAt);
    if (pending.length === 0) {
      toast({ title: "No unmatched remittances to process" });
      return;
    }
    setBatchMatching(true);
    let matched = 0, needsReview = 0, errored = 0;
    for (const r of pending) {
      try {
        const res = await fetch(`${BASE}/api/cash-receipts/remittances/${r.id}/match`, {
          method: "POST", credentials: "include",
          headers: { "X-Requested-With": "fetch" },
        });
        if (!res.ok) { errored++; continue; }
        const body: MatchResponse = await res.json();
        if (body.status === "matched") matched++;
        else if (body.status === "needs_review") needsReview++;
      } catch { errored++; }
    }
    setBatchMatching(false);
    queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
    toast({ title: `Matched ${matched}, needs review ${needsReview}, errors ${errored}` });
  };

  const handleFiles = async (files: File[]) => {
    setUploading(true);
    for (const file of files) {
      try { await uploadMutation.mutateAsync(file); } catch {}
    }
    setUploading(false);
  };

  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Deep-link from the audit log (?remittance=123): once the list has
  // loaded, expand the targeted remittance, scroll to it and briefly
  // highlight it. Runs at most once.
  const remittanceFocusedRef = useRef(false);
  useEffect(() => {
    if (remittanceFocusedRef.current) return;
    const raw = new URLSearchParams(window.location.search).get("remittance");
    if (!raw || !/^\d+$/.test(raw)) return;
    const id = Number(raw);
    if (!remittances.some((r) => r.id === id)) return; // not loaded yet
    remittanceFocusedRef.current = true;
    setExpanded((prev) => new Set(prev).add(id));
    const t = setTimeout(() => {
      const el = document.getElementById(`remittance-${id}`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-primary", "ring-inset", "bg-primary/5");
      setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-inset", "bg-primary/5"), 3000);
    }, 250);
    return () => clearTimeout(t);
  }, [remittances]);

  // In-page "Resolve lines" jump from a blocked BACS row. Unlike the URL
  // deep-link above this can fire repeatedly, so it keys off the prop value
  // rather than a one-shot ref. Waits for the list to load, then expands /
  // scrolls / highlights and clears the request so a repeat click re-fires.
  useEffect(() => {
    if (focusRemittanceId == null) return;
    if (!remittances.some((r) => r.id === focusRemittanceId)) return; // not loaded yet
    setExpanded((prev) => new Set(prev).add(focusRemittanceId));
    const t = setTimeout(() => {
      const el = document.getElementById(`remittance-${focusRemittanceId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-primary", "ring-inset", "bg-primary/5");
        setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-inset", "bg-primary/5"), 3000);
      }
      onFocusHandled?.();
    }, 250);
    return () => clearTimeout(t);
  }, [focusRemittanceId, remittances, onFocusHandled]);

  const handleDelete = (r: Remittance, e: React.MouseEvent) => {
    e.stopPropagation();
    const who = r.customerName || r.customerAccount || r.originalFilename;
    if (!confirm(`Delete remittance from ${who}? This cannot be undone.`)) return;
    deleteMutation.mutate(r.id);
  };

  const KNOWN_STATUSES = ["needs_review", "unmatched", "matched"] as const;

  // Apply filters across all remittances before grouping.
  const filtered = remittances.filter((r) => {
    if (filterAccount) {
      const acc = (r.resolvedAccount || r.customerAccount || r.customerName || "").toLowerCase();
      if (!acc.includes(filterAccount.toLowerCase())) return false;
    }
    if (filterDateFrom && r.remittanceDate && r.remittanceDate < filterDateFrom) return false;
    if (filterDateTo && r.remittanceDate && r.remittanceDate > filterDateTo) return false;
    const amtMin = filterAmountMin !== "" ? parseFloat(filterAmountMin) : null;
    const amtMax = filterAmountMax !== "" ? parseFloat(filterAmountMax) : null;
    if (amtMin !== null && !isNaN(amtMin) && (r.totalAmount ?? 0) < amtMin) return false;
    if (amtMax !== null && !isNaN(amtMax) && (r.totalAmount ?? 0) > amtMax) return false;
    return true;
  });

  // Archived ("already posted in Merlin") remittances keep their underlying
  // status but are pulled out of the active groups into their own collapsible
  // section, so the active lists only show work that still needs doing.
  const active = filtered.filter((r) => !r.archivedAt);
  const grouped: Record<string, Remittance[]> = {
    needs_review: active.filter((r) => r.status === "needs_review"),
    unmatched: active.filter((r) => r.status === "unmatched"),
    matched: active.filter((r) => r.status === "matched"),
    other: active.filter((r) => !KNOWN_STATUSES.includes(r.status as any)),
    archived: filtered.filter((r) => r.archivedAt),
  };

  const pendingCount = active.filter((r) => r.status === "unmatched" && !r.matchAttemptedAt).length;

  return (
    <>
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-3">
          <h2 className="font-semibold text-duffells-grey flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Upload Customer Remittance
          </h2>
          {pendingCount > 0 && (
            <Button onClick={matchAll} disabled={batchMatching} size="sm" variant="outline">
              {batchMatching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
              Match {pendingCount} pending against Merlin
            </Button>
          )}
        </div>
        <p className="text-xs text-duffells-grey/60 mb-3">
          PDF, CSV, XLSX, JPG, PNG, DOC, or email (EML/MSG). Each file is parsed once; duplicates are rejected by content hash.
        </p>
        <FileDropzone
          onUpload={handleFiles}
          accept=".pdf,.csv,.xlsx,.xls,.jpg,.jpeg,.png,.doc,.eml,.msg,application/pdf,text/csv,image/jpeg,image/png,message/rfc822,application/vnd.ms-outlook"
          label="Drop remittance file(s) here"
          multiple={true}
          maxSize={25 * 1024 * 1024}
        />
        {uploading && (
          <div className="mt-3 flex items-center gap-2 text-sm text-duffells-grey">
            <Loader2 className="w-4 h-4 animate-spin" />
            Parsing remittance… (OCR can take 30s+)
          </div>
        )}
      </div>

      {/* Sort & filter bar */}
      {remittances.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <button
            onClick={() => setFilterOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm text-duffells-grey hover:bg-slate-50 transition-colors"
          >
            <span className="flex items-center gap-2 font-medium">
              <Filter className="w-4 h-4" />
              Sort &amp; filter
              {hasActiveFilter && (
                <span className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary text-white text-[10px] font-bold">
                  {[filterAccount, filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax].filter(Boolean).length}
                </span>
              )}
            </span>
            {filterOpen ? <ChevronDown className="w-4 h-4 opacity-50" /> : <ChevronRight className="w-4 h-4 opacity-50" />}
          </button>

          {filterOpen && (
            <div className="border-t border-slate-100 px-4 py-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Account filter */}
                <div>
                  <label className="block text-xs text-duffells-grey/60 mb-1">Account / customer</label>
                  <input
                    type="text"
                    value={filterAccount}
                    onChange={(e) => setFilterAccount(e.target.value)}
                    placeholder="e.g. ABC001"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                {/* Date range */}
                <div>
                  <label className="block text-xs text-duffells-grey/60 mb-1">Date from</label>
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-xs text-duffells-grey/60 mb-1">Date to</label>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                {/* Amount range */}
                <div>
                  <label className="block text-xs text-duffells-grey/60 mb-1">Amount (min – max)</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={filterAmountMin}
                      onChange={(e) => setFilterAmountMin(e.target.value)}
                      placeholder="0"
                      className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    <span className="text-duffells-grey/40 text-xs shrink-0">–</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={filterAmountMax}
                      onChange={(e) => setFilterAmountMax(e.target.value)}
                      placeholder="∞"
                      className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                </div>
              </div>

              {/* Sort row */}
              <div className="flex items-center gap-3 pt-1 flex-wrap">
                <span className="text-xs text-duffells-grey/60 shrink-0">Sort by:</span>
                {(["date", "account", "amount"] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => {
                      if (sortField === f) setSortDir((d) => d === "asc" ? "desc" : "asc");
                      else { setSortField(f); setSortDir(f === "date" ? "desc" : "asc"); }
                    }}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                      sortField === f
                        ? "border-primary bg-primary/5 text-primary"
                        : "border-slate-200 text-duffells-grey/70 hover:border-slate-300"
                    }`}
                  >
                    {f === "date" ? "Date" : f === "account" ? "Account" : "Value"}
                    {sortField === f
                      ? sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      : <ArrowUpDown className="w-3 h-3 opacity-40" />
                    }
                  </button>
                ))}
                {hasActiveFilter && (
                  <button
                    onClick={clearFilters}
                    className="ml-auto flex items-center gap-1 px-3 py-1.5 text-xs text-red-600 hover:text-red-700 border border-red-200 hover:border-red-300 rounded-lg transition-colors"
                  >
                    <X className="w-3 h-3" /> Clear filters
                  </button>
                )}
              </div>

              {hasActiveFilter && (
                <p className="text-xs text-duffells-grey/50">
                  Showing {filtered.length} of {remittances.length} remittance{remittances.length !== 1 ? "s" : ""}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : isError ? (
        <div className="bg-red-50 rounded-2xl border border-red-200 p-6 text-sm text-red-800">
          <p className="font-medium mb-1">Could not load remittances</p>
          <p className="text-xs">{(error as Error)?.message || "Unknown error"}</p>
        </div>
      ) : remittances.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-duffells-grey/60 text-sm">
          No remittances uploaded yet.
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-duffells-grey/60 text-sm">
          No remittances match the current filters.{" "}
          <button onClick={clearFilters} className="text-primary hover:underline">Clear filters</button>
        </div>
      ) : (
        (["needs_review", "unmatched", "matched", "other", "archived"] as const).map((group) =>
          grouped[group].length === 0 ? null : (
            <RemittanceGroup
              key={group}
              status={group}
              remittances={grouped[group]}
              expanded={expanded}
              onToggle={toggleExpand}
              onDelete={handleDelete}
              sortField={sortField}
              sortDir={sortDir}
            />
          ),
        )
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// PostcodeMatchPanel — shown inside an unmatched remittance detail when
// no invoice refs resolved to a Merlin account. Extracts postcodes from the
// stored text, finds matching customers, and lets the operator send a query
// email asking the customer which invoices they meant to pay.
// ---------------------------------------------------------------------------

interface PostcodeMatchResult {
  postcodes: string[];
  candidates: Array<{ account: string; name: string; email: string; postcode: string; nameScore: number }>;
  extractedName: string | null;
}

function PostcodeMatchPanel({ remittance }: { remittance: Remittance }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [chosenIdx, setChosenIdx] = useState<number | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery<PostcodeMatchResult>({
    queryKey: ["cash-receipts", "remittance-postcode-match", remittance.id],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/postcode-match`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      return res.json();
    },
    enabled: open,
    staleTime: 60_000,
  });

  const sendMutation = useMutation({
    mutationFn: async ({ account, email, toName }: { account: string; email: string; toName?: string }) => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/send-postcode-request`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({ account, email, toName }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body as { ok: boolean; sentTo: string; account: string };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({ title: `Query email sent to ${result.sentTo} (${result.account})` });
      setOpen(false);
    },
    onError: (err: any) => toast({ title: err.message || "Failed to send email", variant: "destructive" }),
  });

  if (!open) {
    return (
      <div className="mb-3">
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className="text-xs text-primary hover:text-primary/80 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-primary/5"
          title="Search for the customer by postcode extracted from the remittance document"
        >
          <MapPin className="w-3.5 h-3.5" /> Look up customer by postcode
        </button>
      </div>
    );
  }

  const chosen = data?.candidates[chosenIdx ?? -1] ?? null;

  return (
    <div className="mb-3 border border-slate-200 rounded-lg bg-white p-3 text-xs" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-duffells-grey flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-primary" /> Customer postcode lookup
        </h4>
        <button onClick={() => setOpen(false)} className="text-duffells-grey/50 hover:text-duffells-grey">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-duffells-grey/60 py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching Merlin…
        </div>
      ) : isError ? (
        <div className="px-3 py-2 rounded bg-red-50 border border-red-200 text-red-700">
          {(error as Error)?.message || "Lookup failed"}.{" "}
          <button onClick={() => refetch()} className="underline">Retry</button>
        </div>
      ) : !data ? null : (
        <>
          {data.postcodes.length === 0 ? (
            <div className="text-duffells-grey/60 py-2">
              No UK postcodes found in the remittance text. The document may need a manual search.
            </div>
          ) : (
            <>
              <div className="text-duffells-grey/60 mb-2">
                Postcode{data.postcodes.length !== 1 ? "s" : ""} found:{" "}
                {data.postcodes.map((pc) => (
                  <code key={pc} className="ml-1 font-mono bg-slate-100 px-1 rounded">{pc}</code>
                ))}
                {data.extractedName && (
                  <span className="ml-2">— name extracted: <strong>{data.extractedName}</strong></span>
                )}
              </div>

              {data.candidates.length === 0 ? (
                <div className="text-duffells-grey/60 py-1">
                  No Merlin customers found with {data.postcodes.length === 1 ? "this postcode" : "these postcodes"}.
                </div>
              ) : (
                <div className="space-y-1.5 mb-3">
                  {data.candidates.map((c, idx) => {
                    const isChosen = chosenIdx === idx;
                    const score = Math.round(c.nameScore * 100);
                    return (
                      <label
                        key={c.account}
                        className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors ${
                          isChosen ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name={`postcode-candidate-${remittance.id}`}
                          checked={isChosen}
                          onChange={() => setChosenIdx(idx)}
                          className="mt-0.5 accent-primary"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-semibold text-emerald-700">{c.account}</span>
                            <span className="font-medium text-duffells-grey truncate">{c.name}</span>
                            {score > 0 && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                score >= 60 ? "bg-emerald-100 text-emerald-700" :
                                score >= 30 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                              }`}>
                                {score}% name match
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-duffells-grey/60">
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.postcode}</span>
                            {c.email && (
                              <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {chosen && (
            <div className="border-t border-slate-100 pt-3 mt-1">
              {!chosen.email ? (
                <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  No email address on file for <strong>{chosen.account}</strong> in Merlin. You'll need to contact them manually.
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-duffells-grey/70">
                    Send a query to{" "}
                    <strong>{chosen.name}</strong> ({chosen.account}) at{" "}
                    <code className="bg-slate-100 px-1 rounded">{chosen.email}</code>{" "}
                    asking which invoices this remittance covers. Open invoices on the account will be listed in the email.
                  </div>
                  <button
                    onClick={() => sendMutation.mutate({ account: chosen.account, email: chosen.email, toName: chosen.name })}
                    disabled={sendMutation.isPending}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 px-3 py-1.5 rounded"
                  >
                    {sendMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Send query email to {chosen.email}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RemittanceGroup({
  status,
  remittances,
  expanded,
  onToggle,
  onDelete,
  sortField,
  sortDir,
}: {
  status: string;
  remittances: Remittance[];
  expanded: Set<number>;
  onToggle: (id: number) => void;
  onDelete: (r: Remittance, e: React.MouseEvent) => void;
  sortField: "date" | "account" | "amount";
  sortDir: "asc" | "desc";
}) {
  const titles: Record<string, string> = {
    needs_review: "Needs review",
    unmatched: "Unmatched (awaiting bank payment match)",
    matched: "Matched",
    other: "Other / unknown status",
    archived: "Already posted in Merlin (archived)",
  };

  // Matched and archived groups start collapsed — they grow large and are
  // lower priority than active-work groups. All others start open.
  const startsCollapsed = status === "matched" || status === "archived";
  const [groupOpen, setGroupOpen] = useState(!startsCollapsed);
  // Inline search only on the large groups where it's needed.
  const [groupSearch, setGroupSearch] = useState("");

  const sorted = [...remittances].sort((a, b) => {
    let cmp = 0;
    if (sortField === "date") {
      const da = a.remittanceDate ?? "";
      const db = b.remittanceDate ?? "";
      cmp = da < db ? -1 : da > db ? 1 : 0;
    } else if (sortField === "account") {
      const aa = (a.resolvedAccount || a.customerAccount || a.customerName || "").toLowerCase();
      const ab = (b.resolvedAccount || b.customerAccount || b.customerName || "").toLowerCase();
      cmp = aa < ab ? -1 : aa > ab ? 1 : 0;
    } else {
      cmp = (a.totalAmount ?? 0) - (b.totalAmount ?? 0);
    }
    return sortDir === "asc" ? cmp : -cmp;
  });

  // Filter by the inline search term (account, customer name, match notes).
  const q = groupSearch.trim().toLowerCase();
  const displayed = q
    ? sorted.filter((r) => {
        const haystack = [
          r.resolvedAccount,
          r.customerAccount,
          r.customerName,
          r.matchNotes,
          r.remittanceDate,
        ].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(q);
      })
    : sorted;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      {/* Clickable header — toggles the group open/closed */}
      <button
        className="w-full px-6 py-4 border-b border-slate-100 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
        onClick={() => setGroupOpen((v) => !v)}
      >
        <h2 className="font-semibold text-duffells-grey flex items-center gap-2">
          {groupOpen ? <ChevronDown className="w-4 h-4 text-duffells-grey/50" /> : <ChevronRight className="w-4 h-4 text-duffells-grey/50" />}
          {titles[status]}
          <span className="text-xs font-normal text-duffells-grey/60">({remittances.length})</span>
        </h2>
        <div onClick={(e) => e.stopPropagation()}>
          {statusPill(status)}
        </div>
      </button>

      {groupOpen && (
        <>
          {/* Inline search — shown for matched / archived where the list grows large */}
          {(status === "matched" || status === "archived") && (
            <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/60">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-duffells-grey/40" />
                <input
                  type="text"
                  value={groupSearch}
                  onChange={(e) => setGroupSearch(e.target.value)}
                  placeholder="Filter by account, name, invoice number or date…"
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
                  onClick={(e) => e.stopPropagation()}
                />
                {groupSearch && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setGroupSearch(""); }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-duffells-grey/40 hover:text-duffells-grey"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              {q && (
                <p className="text-[11px] text-duffells-grey/50 mt-1">
                  {displayed.length} of {sorted.length} shown
                </p>
              )}
            </div>
          )}

          {displayed.length === 0 ? (
            <div className="px-6 py-4 text-sm text-duffells-grey/60">
              No remittances match "<strong>{groupSearch}</strong>".{" "}
              <button onClick={() => setGroupSearch("")} className="text-primary hover:underline">Clear</button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/70">
                <tr>
                  <th className="px-3 py-3 w-8"></th>
                  <th className="px-3 py-3 text-left">Customer</th>
                  <th className="px-3 py-3 text-left">Account</th>
                  <th className="px-3 py-3 text-left">Date</th>
                  <th className="px-3 py-3 text-right">Total</th>
                  <th className="px-3 py-3 text-right">Lines</th>
                  <th className="px-3 py-3 text-left">Source</th>
                  <th className="px-3 py-3 text-left">Filename</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayed.map((r) => (
                  <RemittanceRow
                    key={r.id}
                    remittance={r}
                    isExpanded={expanded.has(r.id)}
                    onToggle={() => onToggle(r.id)}
                    onDelete={(e) => onDelete(r, e)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

interface ColumnStatsResponse {
  sourceFormat: string;
  headers: string[];
  rowCount: number;
  columnSums: Record<string, number | null>;
  sampleValues: Record<string, string[]>;
  chosenColumns: { amount?: string; reference?: string; date?: string } | null;
  printedTotal: number | null;
  customerAccount: string | null;
  resolvedAccount: string | null;
  templateKey: string | null;
  savedTemplate: {
    customerAccount: string;
    amountColumn: string | null;
    referenceColumn: string | null;
    dateColumn: string | null;
    signConvention: string | null;
    secondaryRefHandling: string | null;
    dateFormat: string | null;
    parseInstructions: string | null;
    sourceFormat: string | null;
    updatedAt: string;
  } | null;
}

// Per-column picker that lets operators correct misparsed CSV/XLSX
// remittances (TRA017 case: parser picked PO_Number=£102m instead of
// Gross=£76k) and persist the correction as a per-customer-account
// template that auto-applies to future uploads.
function ColumnPickerPanel({
  remittanceId,
  isMatched,
  onReparsed,
}: {
  remittanceId: number;
  isMatched: boolean;
  onReparsed: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState<string>("");
  const [reference, setReference] = useState<string>("");
  const [dateCol, setDateCol] = useState<string>("");
  const [saveTemplate, setSaveTemplate] = useState(true);

  const { data, isLoading, isError, error } = useQuery<ColumnStatsResponse>({
    queryKey: ["cash-receipts", "remittance-column-stats", remittanceId],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittanceId}/column-stats`, { credentials: "include" });
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Column stats unavailable");
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load column stats (HTTP ${res.status})`);
      }
      return res.json();
    },
    enabled: open,
    staleTime: 30_000,
  });

  // Initialise the radio/dropdowns to whatever's currently in use the
  // first time the panel data arrives, so operators see the existing
  // pick highlighted and only have to change what's wrong.
  useEffect(() => {
    if (!data) return;
    // Saved template wins (it's what'll be applied to future uploads); fall
    // back to whatever the heuristic picked on the current parse.
    const tpl = data.savedTemplate;
    const cur = data.chosenColumns;
    const a = tpl?.amountColumn ?? cur?.amount ?? "";
    const r = tpl?.referenceColumn ?? cur?.reference ?? "";
    const d = tpl?.dateColumn ?? cur?.date ?? "";
    if (a) setAmount(a);
    setReference(r ?? "");
    setDateCol(d ?? "");
  }, [data]);

  const reparseMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittanceId}/reparse-with-columns`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({
          amountColumn: amount,
          referenceColumn: reference || undefined,
          dateColumn: dateCol || undefined,
          saveTemplate,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Re-parse failed (HTTP ${res.status})`);
      }
      return res.json() as Promise<{
        lineCount: number;
        templateSaved: boolean;
        templateKey: string | null;
      }>;
    },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      onReparsed();
      const tplMsg = r.templateSaved
        ? ` — saved as template for ${r.templateKey}`
        : saveTemplate && !r.templateSaved
          ? " — template NOT saved (no customer account on this remittance)"
          : "";
      toast({ title: `Re-parsed: ${r.lineCount} line(s)${tplMsg}` });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  if (!open) {
    return (
      <div className="mb-3">
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className="text-xs text-primary hover:text-primary/80 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-primary/5"
          title="Pick which column is the amount and save it as a template for this customer"
        >
          <Settings className="w-3.5 h-3.5" /> Fix columns / save template
        </button>
      </div>
    );
  }

  return (
    <div className="mb-3 border border-slate-200 rounded-lg bg-white p-3" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-duffells-grey">Columns in this file</h4>
        <button
          onClick={() => setOpen(false)}
          className="text-xs text-duffells-grey/60 hover:text-duffells-grey"
        >
          Close
        </button>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>
      ) : isError ? (
        <div className="text-sm px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700">
          {(error as Error)?.message || "Failed to load column stats"}
        </div>
      ) : !data ? null : (
        <>
          <div className="text-xs text-duffells-grey/70 mb-2">
            {data.rowCount} row(s) detected. Printed total on this remittance:{" "}
            <strong>{data.printedTotal != null ? fmtMoney(data.printedTotal) : "—"}</strong>.
            Pick the column whose sum matches it.
            {data.savedTemplate && (
              <span className="ml-2 text-emerald-700">
                (Saved template: amount=<code>{data.savedTemplate.amountColumn}</code>)
              </span>
            )}
          </div>
          <table className="w-full text-xs border border-slate-200 rounded">
            <thead className="bg-slate-50 text-duffells-grey/70">
              <tr>
                <th className="px-2 py-1 text-left w-16">Amount</th>
                <th className="px-2 py-1 text-left w-16">Ref</th>
                <th className="px-2 py-1 text-left w-16">Date</th>
                <th className="px-2 py-1 text-left">Column</th>
                <th className="px-2 py-1 text-right">Sum</th>
                <th className="px-2 py-1 text-left">Sample values</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.headers.map((h) => {
                const sum = data.columnSums[h];
                const samples = data.sampleValues[h] || [];
                const isAmountMatch =
                  sum != null && data.printedTotal != null
                  && Math.abs(sum - data.printedTotal) < 0.01;
                return (
                  <tr key={h} className={isAmountMatch ? "bg-emerald-50" : ""}>
                    <td className="px-2 py-1">
                      <input
                        type="radio"
                        name={`amount-${remittanceId}`}
                        checked={amount === h}
                        onChange={() => setAmount(h)}
                        disabled={sum == null}
                        title={sum == null ? "Column has non-numeric values" : "Use as amount column"}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="radio"
                        name={`ref-${remittanceId}`}
                        checked={reference === h}
                        onChange={() => setReference(reference === h ? "" : h)}
                        onClick={() => setReference(reference === h ? "" : h)}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="radio"
                        name={`date-${remittanceId}`}
                        checked={dateCol === h}
                        onChange={() => setDateCol(dateCol === h ? "" : h)}
                        onClick={() => setDateCol(dateCol === h ? "" : h)}
                      />
                    </td>
                    <td className="px-2 py-1 font-mono text-duffells-grey">
                      {h}
                      {isAmountMatch && <span className="ml-1 text-emerald-700">✓ matches total</span>}
                    </td>
                    <td className="px-2 py-1 text-right font-mono">
                      {sum == null ? <span className="text-slate-400">—</span> : fmtMoney(sum)}
                    </td>
                    <td className="px-2 py-1 text-duffells-grey/60 font-mono truncate max-w-[280px]">
                      {samples.join(" · ")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-3 flex items-center gap-3">
            <label
              className={`text-xs inline-flex items-center gap-1 ${data.templateKey ? "text-duffells-grey" : "text-slate-400"}`}
              title={data.templateKey
                ? `Save as the default for customer account ${data.templateKey} — future uploads from this account will use these columns automatically.`
                : "No customer account detected on this remittance — cannot save a template."}
            >
              <input
                type="checkbox"
                checked={saveTemplate && !!data.templateKey}
                onChange={(e) => setSaveTemplate(e.target.checked)}
                disabled={!data.templateKey}
              />
              Save as template for account{" "}
              <code className="text-duffells-grey">{data.templateKey ?? "—"}</code>
            </label>
            <div className="flex-1" />
            <button
              onClick={() => reparseMutation.mutate()}
              disabled={!amount || reparseMutation.isPending || isMatched}
              className="text-xs text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1 px-3 py-1.5 rounded"
              title={isMatched ? "Unlink this remittance from its bank payment first." : amount ? "Re-parse using the selected columns" : "Pick an amount column"}
            >
              {reparseMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Re-parse with these columns
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Per-customer parse-rules editor. Unlike ColumnPickerPanel (which only
// corrects which column holds the amount/ref for CSV/XLSX), this captures a
// plain-English rule that an AI applies to the extracted lines (sign handling,
// credit notes, secondary refs, dates) and works for PDF positional parsing
// too. Re-parsing runs the generalised reconciliation guard, so the returned
// sum-vs-stated-total delta is shown to the operator and a divergence forces
// the remittance into needs_review.
function ParseProfilePanel({
  remittance,
  onReparsed,
}: {
  remittance: Remittance;
  onReparsed: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [saveProfile, setSaveProfile] = useState(true);
  const [lastResult, setLastResult] = useState<{
    requiresManualReview: boolean;
    statedTotal: number | null;
    sumLines: number;
    delta: number | null;
    reconciliationNote: string | null;
    lineCount: number;
    profileSaved: boolean;
  } | null>(null);

  const isMatched = remittance.status === "matched" || remittance.matchedBankPaymentId != null;
  const canSaveProfile = !!remittance.customerAccount;

  const reparseMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/reparse-with-profile`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({
          parseInstructions: instructions.trim() || undefined,
          saveProfile: saveProfile && canSaveProfile,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Re-parse failed (HTTP ${res.status})`);
      return body as {
        lineCount: number;
        requiresManualReview: boolean;
        statedTotal: number | null;
        sumLines: number;
        delta: number | null;
        reconciliationNote: string | null;
        profileSaved: boolean;
        templateKey: string | null;
      };
    },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      onReparsed();
      setLastResult({
        requiresManualReview: r.requiresManualReview,
        statedTotal: r.statedTotal,
        sumLines: r.sumLines,
        delta: r.delta,
        reconciliationNote: r.reconciliationNote,
        lineCount: r.lineCount,
        profileSaved: r.profileSaved,
      });
      const savedMsg = r.profileSaved ? ` — profile saved for ${r.templateKey}` : "";
      toast({
        title: r.requiresManualReview
          ? `Re-parsed ${r.lineCount} line(s) — flagged for review`
          : `Re-parsed: ${r.lineCount} line(s)${savedMsg}`,
        variant: r.requiresManualReview ? "destructive" : undefined,
      });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const noKnobSet = !instructions.trim();

  if (!open) {
    return (
      <div className="mb-3">
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className="text-xs text-primary hover:text-primary/80 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-primary/5"
          title="Type plain-English rules for how this customer's remittances should be read, and save them as their profile"
        >
          <Settings className="w-3.5 h-3.5" /> Parse rules
        </button>
      </div>
    );
  }

  return (
    <div className="mb-3 border border-slate-200 rounded-lg bg-white p-3" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-duffells-grey">Parse rules</h4>
        <button onClick={() => setOpen(false)} className="text-xs text-duffells-grey/60 hover:text-duffells-grey">Close</button>
      </div>
      <div className="text-xs text-duffells-grey/70 mb-3">
        Describe in plain English how this customer's remittance should be interpreted. An AI applies your
        rules to the extracted lines, and the result is still checked against the document's total before
        it can be matched. Leave blank to use the automatic heuristics.
        {!canSaveProfile && <span className="ml-1 text-amber-600">No customer account on this remittance — can't save a reusable profile.</span>}
      </div>
      <label className="text-xs text-duffells-grey block">
        <span className="block mb-1 font-medium">Rules</span>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={4}
          placeholder={'e.g. "If the Ref column starts with \\"credit\\", ignore the invoice reference in that column and use the credit-note reference in the 2nd Ref column."'}
          className="w-full text-xs px-2 py-1.5 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </label>
      {lastResult && (
        <div
          className={`mt-3 text-xs px-3 py-2 rounded-lg border ${
            lastResult.requiresManualReview
              ? "bg-amber-50 border-amber-200 text-amber-800"
              : "bg-emerald-50 border-emerald-200 text-emerald-800"
          }`}
        >
          <div className="font-medium">
            {lastResult.lineCount} line(s) · sum {fmtMoney(lastResult.sumLines)}
            {lastResult.statedTotal != null && <> vs stated {fmtMoney(lastResult.statedTotal)}</>}
            {lastResult.delta != null && Math.abs(lastResult.delta) >= 0.01 && <> · Δ {fmtMoney(lastResult.delta)}</>}
          </div>
          {lastResult.reconciliationNote && <div className="mt-0.5">{lastResult.reconciliationNote}</div>}
          {lastResult.requiresManualReview && <div className="mt-0.5">Flagged for review — resolve the discrepancy before matching.</div>}
        </div>
      )}
      <div className="mt-3 flex items-center gap-3">
        <label
          className={`text-xs inline-flex items-center gap-1 ${canSaveProfile ? "text-duffells-grey" : "text-slate-400"}`}
          title={canSaveProfile
            ? `Save as the parse profile for customer account ${remittance.customerAccount} — future uploads from this account use it automatically.`
            : "No customer account detected on this remittance — cannot save a profile."}
        >
          <input
            type="checkbox"
            checked={saveProfile && canSaveProfile}
            onChange={(e) => setSaveProfile(e.target.checked)}
            disabled={!canSaveProfile}
          />
          Save as profile for account <code className="text-duffells-grey">{remittance.customerAccount ?? "—"}</code>
        </label>
        <div className="flex-1" />
        <button
          onClick={() => reparseMutation.mutate()}
          disabled={noKnobSet || reparseMutation.isPending || isMatched}
          className="text-xs text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1 px-3 py-1.5 rounded"
          title={isMatched ? "Unlink this remittance from its bank payment first." : noKnobSet ? "Type a rule first." : "Re-parse using these rules"}
        >
          {reparseMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Re-parse with rules
        </button>
      </div>
    </div>
  );
}

function RemittanceRow({
  remittance,
  isExpanded,
  onToggle,
  onDelete,
}: {
  remittance: Remittance;
  isExpanded: boolean;
  onToggle: () => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [lastMatch, setLastMatch] = useState<MatchResponse | null>(null);

  const { data: detail, isLoading, isError, error } = useQuery<RemittanceDetail>({
    queryKey: ["cash-receipts", "remittance-detail", remittance.id],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load remittance detail (HTTP ${res.status})`);
      }
      return res.json();
    },
    enabled: isExpanded,
  });

  const matchMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/match`, {
        method: "POST", credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Match failed (HTTP ${res.status})`);
      }
      return (await res.json()) as MatchResponse;
    },
    onSuccess: (data) => {
      setLastMatch(data);
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      if (data.archived) {
        toast({
          title: "Archived — already posted in Merlin",
          description: `${data.archived.settledCount} of ${data.archived.totalLines} invoice(s) already settled.`,
        });
        return;
      }
      const msg = data.status === "matched"
        ? `Matched to bank payment #${data.autoMatchedBankPaymentId}`
        : data.status === "needs_review"
          ? `Needs review (${data.candidates.length} candidate(s))`
          : "No bank payment found";
      toast({ title: msg });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  // Manual "Check Merlin" — archives the remittance if any of its invoices are
  // already settled in Merlin (any settled level; operator override).
  const checkPostedMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/check-posted`, {
        method: "POST", credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Check failed (HTTP ${res.status})`);
      return body as { archived: boolean; message: string };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({ title: data.archived ? "Archived as already posted" : "Nothing posted yet", description: data.message });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const unarchiveMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/unarchive`, {
        method: "POST", credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Unarchive failed (HTTP ${res.status})`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({ title: "Restored to active list" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const confirmMutation = useMutation({
    mutationFn: async (bankPaymentId: number) => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/confirm-match`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({ bankPaymentId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Confirm failed (HTTP ${res.status})`);
      }
    },
    onSuccess: () => {
      setLastMatch(null);
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({ title: "Match confirmed" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const lineAccountMutation = useMutation({
    mutationFn: async ({ lineId, account }: { lineId: number; account: string | null }) => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/lines/${lineId}/account`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({ account }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const e: any = new Error(body.error || `HTTP ${res.status}`);
        e.status = res.status;
        throw e;
      }
      return body as RemittanceLine & { warning?: string | null };
    },
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      if (data?.warning) {
        toast({ title: "Account saved with a warning", description: data.warning, variant: "destructive" });
      } else {
        toast({ title: vars.account ? `Line account set to ${vars.account}` : "Line account cleared" });
      }
    },
    onError: (err: any) => {
      // The server locks line accounts once the payment has begun posting to
      // Merlin (409). Show a clear explanation and refresh the detail so the
      // fields visibly lock, rather than a generic failure toast.
      if (err?.status === 409) {
        queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
        toast({
          title: "Account locked",
          description:
            err.message ||
            "This payment has already started posting to Merlin, so its line accounts can no longer be changed.",
          variant: "destructive",
        });
        return;
      }
      toast({ title: err.message || "Failed to set line account", variant: "destructive" });
    },
  });

  // Correct a line's customer reference (e.g. a mistyped invoice number). The
  // server re-verifies it against the line's existing account, so a "ref not
  // found" badge clears in place when the right number is supplied.
  const referenceMutation = useMutation({
    mutationFn: async ({ lineId, reference }: { lineId: number; reference: string }) => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/lines/${lineId}/reference`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({ reference }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const e: any = new Error(body.error || `HTTP ${res.status}`);
        e.status = res.status;
        throw e;
      }
      return body as RemittanceLine & { warning?: string | null };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      if (data?.warning) {
        toast({ title: "Reference updated — still flagged", description: data.warning, variant: "destructive" });
      } else {
        toast({ title: "Reference updated" });
      }
    },
    onError: (err: any) => {
      if (err?.status === 409) {
        queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
        toast({
          title: "Line locked",
          description:
            err.message ||
            "This payment has already started posting to Merlin, so its lines can no longer be changed.",
          variant: "destructive",
        });
        return;
      }
      toast({ title: err.message || "Failed to update reference", variant: "destructive" });
    },
  });

  const amountCorrectionMutation = useMutation({
    mutationFn: async ({ lineId }: { lineId: number }) => {
      const res = await fetch(
        `${BASE}/api/cash-receipts/remittances/${remittance.id}/lines/${lineId}/use-merlin-amount`,
        {
          method: "POST",
          credentials: "include",
          headers: { "X-Requested-With": "fetch" },
        },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const error: any = new Error(body.error || `HTTP ${res.status}`);
        error.status = res.status;
        throw error;
      }
      return body as {
        line: RemittanceLine;
        totalAmount: number;
        match: MatchResponse | null;
        matchError: string | null;
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      if (data.match?.status === "matched") {
        toast({
          title: `Amount corrected — matched to £${data.totalAmount.toFixed(2)} payment`,
          description: `Linked to bank payment #${data.match.autoMatchedBankPaymentId}.`,
        });
      } else {
        toast({
          title: "Amount corrected and remittance recalculated",
          description: data.matchError
            ? `New remittance total: ${fmtMoney(data.totalAmount)}. Automatic matching could not be retried: ${data.matchError}`
            : `New remittance total: ${fmtMoney(data.totalAmount)}. Matching was retried.`,
        });
      }
    },
    onError: (err: any) => {
      if (err?.status === 409) {
        queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      }
      toast({ title: err.message || "Failed to correct amount", variant: "destructive" });
    },
  });

  // Re-run the Merlin verify for a flagged line in place — no clear-and-retype.
  // Tracks the specific lineId so only that row shows "checking…".
  const [recheckingLineId, setRecheckingLineId] = useState<number | null>(null);
  const recheckMutation = useMutation({
    mutationFn: async ({ lineId }: { lineId: number }) => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/lines/${lineId}/recheck`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body as RemittanceLine & { warning?: string | null };
    },
    onMutate: ({ lineId }) => setRecheckingLineId(lineId),
    onSettled: () => setRecheckingLineId(null),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      if (data?.warning) {
        toast({ title: "Re-checked — still flagged", description: data.warning, variant: "destructive" });
      } else {
        toast({ title: "Re-checked — account verifies cleanly" });
      }
    },
    onError: (err: any) => toast({ title: err.message || "Re-check failed", variant: "destructive" }),
  });

  // Manually match a line to a real outstanding invoice picked from the
  // account's Merlin ledger — the unrecoverable-typo fallback. Sets the line's
  // reference + account and re-verifies, same 409 posting-started lock as the
  // reference/account PATCHes.
  const matchInvoiceMutation = useMutation({
    mutationFn: async ({ lineId, reference }: { lineId: number; reference: string }) => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/lines/${lineId}/match-invoice`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({ reference }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const e: any = new Error(body.error || `HTTP ${res.status}`);
        e.status = res.status;
        throw e;
      }
      return body as RemittanceLine & { warning?: string | null };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      if (data?.warning) {
        toast({ title: "Matched — still flagged", description: data.warning, variant: "destructive" });
      } else {
        toast({ title: "Line matched to invoice" });
      }
    },
    onError: (err: any) => {
      if (err?.status === 409) {
        queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
        toast({
          title: "Line locked",
          description:
            err.message ||
            "This payment has already started posting to Merlin, so its lines can no longer be changed.",
          variant: "destructive",
        });
        return;
      }
      toast({ title: err.message || "Failed to match invoice", variant: "destructive" });
    },
  });

  // Book a single review line on-account (sltranid=-1) for its remittance
  // amount. Used to clear a line whose ref can't be matched to a Merlin invoice
  // when editing the ref / matching an invoice isn't the right answer — the cash
  // is genuinely unallocated. Removes the line from the review set; a later
  // "Post remaining" finalises the payment.
  const payUnallocatedMutation = useMutation({
    mutationFn: async ({ lineId }: { lineId: number }) => {
      const bankPaymentId = remittance.matchedBankPaymentId;
      if (bankPaymentId == null) throw new Error("This remittance is not linked to a bank payment");
      const res = await fetch(
        `${BASE}/api/cash-receipts/bank-payments/${bankPaymentId}/lines/${lineId}/pay-unallocated`,
        { method: "POST", credentials: "include", headers: { "X-Requested-With": "fetch" } },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body as { ok: boolean; result: { sourceRef: string; account: string; amount: number } };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({
        title: "Line booked on-account",
        description: `${data.result.sourceRef}: ${fmtMoney(data.result.amount)} to ${data.result.account}`,
      });
    },
    onError: (err: any) => toast({ title: err.message || "Failed to pay line unallocated", variant: "destructive" }),
  });

  const unlinkMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/unlink`, {
        method: "POST", credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      if (!res.ok) throw new Error("Unlink failed");
    },
    onSuccess: () => {
      setLastMatch(null);
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({ title: "Match removed" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const reparseMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/reparse`, {
        method: "POST", credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Reparse failed (HTTP ${res.status})`);
      }
      return (await res.json()) as { success: boolean; lineCount: number; match: MatchResponse | null };
    },
    onSuccess: (data) => {
      if (data.match) setLastMatch(data.match);
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({ title: `Re-parsed: ${data.lineCount} line(s) extracted` });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  // Option C — assisted handwritten-statement allocation. Only ever fetched
  // when the row carries a handwritten amount; null on every normal remittance.
  const hasHandwritten = remittance.handwrittenPayAmount != null;
  const { data: suggestion, isLoading: suggestionLoading } = useQuery<SuggestedAllocationResponse>({
    queryKey: ["cash-receipts", "suggested-allocation", remittance.id],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/suggested-allocation`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load suggestion (HTTP ${res.status})`);
      }
      return res.json();
    },
    enabled: isExpanded && hasHandwritten && remittance.status !== "matched",
  });

  const acceptSuggestionMutation = useMutation({
    mutationFn: async (lines: Array<{ ref: string; applied: number }>) => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittance.id}/accept-suggestion`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({ lines }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Accept failed (HTTP ${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
      toast({ title: "Allocation accepted — now run Match to find the bank payment" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  // Editable copy of the suggested allocation. The operator can deselect
  // invoices or change applied amounts before accepting; we re-validate
  // server-side. Re-seeded whenever a fresh suggestion arrives.
  // The operator can also ADD an outstanding invoice the suggestion didn't
  // reach: every outstanding invoice is seeded, the suggested ones selected
  // with their applied amount and the rest unselected (defaulting to their full
  // balance) ready to be ticked on.
  const [edits, setEdits] = useState<Record<string, { selected: boolean; applied: string }>>({});
  useEffect(() => {
    if (suggestion?.available && suggestion.outstanding) {
      const suggestedApplied = new Map(
        (suggestion.allocation?.lines ?? []).map((l) => [l.ref, l.applied]),
      );
      const seeded: Record<string, { selected: boolean; applied: string }> = {};
      for (const inv of suggestion.outstanding) {
        const applied = suggestedApplied.get(inv.ref);
        seeded[inv.ref] =
          applied != null
            ? { selected: true, applied: applied.toFixed(2) }
            : { selected: false, applied: inv.balance.toFixed(2) };
      }
      setEdits(seeded);
    }
  }, [suggestion]);

  const handleMatch = (e: React.MouseEvent) => { e.stopPropagation(); matchMutation.mutate(); };
  const handleUnlink = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm("Remove this match? The bank payment will become unmatched again.")) return;
    unlinkMutation.mutate();
  };
  const handleReparse = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm("Re-extract lines from the stored PDF text and re-run the match? Existing lines on this remittance will be replaced.")) return;
    reparseMutation.mutate();
  };

  const lineSum = detail?.lines.reduce((a, l) => a + l.amount, 0) ?? 0;
  const total = remittance.totalAmount ?? 0;
  const mismatch = detail && Math.abs(lineSum - total) > 0.01;

  // Multi-account posting: a single BACS payment settling invoices across
  // several Merlin accounts. Posting groups by the per-line account, so every
  // postable line MUST resolve to its own account or the server-side post
  // throws ("resolve them manually before posting"). Surface that up front.
  // A remittance is multi-account either because matching set the payment-level
  // sentinel, or because its resolved lines already span >= 2 accounts.
  const resolvedAccounts = new Set(
    (detail?.lines ?? [])
      .filter((l) => l.merlinCheckedAt && !l.merlinNotFound && l.merlinAccount)
      .map((l) => l.merlinAccount as string),
  );
  const isMultiAccount =
    remittance.resolvedAccount === MULTI_ACCOUNT_SENTINEL || resolvedAccounts.size >= 2;
  // A line is unresolved when it carries no Merlin account (never checked, or
  // checked and not found). These are the lines that block a multi-account post.
  const unresolvedLines = (detail?.lines ?? []).filter(
    (l) => !l.merlinCheckedAt || l.merlinNotFound || !l.merlinAccount,
  );
  // Once the matched payment has begun posting to Merlin, the per-line account
  // PATCH route rejects edits (409). Lock the line account fields up front so
  // the operator never attempts an edit that's guaranteed to fail.
  const accountsLocked = !!detail?.matchedPaymentPostingStartedAt;

  return (
    <>
      <tr id={`remittance-${remittance.id}`} onClick={onToggle} className="cursor-pointer hover:bg-slate-50 transition-colors">
        <td className="px-3 py-3 text-duffells-grey/60">
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </td>
        <td className="px-3 py-3 text-sm text-duffells-grey font-medium max-w-[220px] truncate">
          {remittance.customerName || <span className="text-slate-400">—</span>}
        </td>
        <td className="px-3 py-3 text-sm font-mono">
          {remittance.resolvedAccount === MULTI_ACCOUNT_SENTINEL ? (
            <span className="text-amber-700 font-semibold flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> multiple
            </span>
          ) : remittance.resolvedAccount ? (
            <span className="text-emerald-700 font-semibold flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> {remittance.resolvedAccount}
            </span>
          ) : remittance.customerAccount ? (
            <span className="text-duffells-grey/70">{remittance.customerAccount}</span>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="px-3 py-3 text-sm text-duffells-grey whitespace-nowrap">{fmtDate(remittance.remittanceDate)}</td>
        <td className="px-3 py-3 text-sm text-duffells-grey text-right font-medium whitespace-nowrap">
          {remittance.totalAmount != null ? fmtMoney(remittance.totalAmount) : <span className="text-slate-400">—</span>}
        </td>
        <td className="px-3 py-3 text-sm text-duffells-grey text-right">{remittance.lineCount}</td>
        <td className="px-3 py-3 text-xs uppercase text-duffells-grey/60">{remittance.sourceFormat}</td>
        <td className="px-3 py-3 text-xs text-duffells-grey/60 truncate max-w-[200px]">{remittance.originalFilename}</td>
        <td className="px-3 py-3 text-right whitespace-nowrap">
          <div className="flex items-center justify-end gap-1">
            {remittance.archivedAt ? (
              <>
                {remittance.merlinSettledCount != null && (
                  <span
                    className="text-xs text-indigo-700 mr-1"
                    title={
                      remittance.merlinPostingConfidence === "fully_posted"
                        ? "All / full value already posted in Merlin"
                        : "Some invoices already posted in Merlin"
                    }
                  >
                    {remittance.merlinSettledCount} settled
                    {remittance.merlinSettledAmount != null ? ` · ${fmtMoney(remittance.merlinSettledAmount)}` : ""}
                  </span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); unarchiveMutation.mutate(); }}
                  disabled={unarchiveMutation.isPending}
                  className="text-xs text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-indigo-50"
                  title="Restore this remittance to the active list"
                >
                  {unarchiveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Un-archive
                </button>
              </>
            ) : remittance.status === "matched" ? (
              <button
                onClick={handleUnlink}
                disabled={unlinkMutation.isPending}
                className="text-xs text-amber-700 hover:text-amber-900 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-amber-50"
                title="Remove match"
              >
                <Link2Off className="w-3.5 h-3.5" /> Unlink
              </button>
            ) : (
              <>
                <button
                  onClick={handleReparse}
                  disabled={reparseMutation.isPending}
                  className="text-xs text-duffells-grey hover:text-duffells-grey/70 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100"
                  title="Re-extract lines from the stored PDF text (use after a parser fix without re-uploading)"
                >
                  {reparseMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Reparse
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); checkPostedMutation.mutate(); }}
                  disabled={checkPostedMutation.isPending}
                  className="text-xs text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-indigo-50"
                  title="Check Merlin: if these invoices are already posted, archive this remittance"
                >
                  {checkPostedMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Check Merlin
                </button>
                <button
                  onClick={handleMatch}
                  disabled={matchMutation.isPending}
                  className="text-xs text-primary hover:text-primary/80 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-primary/5"
                  title={remittance.matchAttemptedAt ? "Re-run match" : "Match against Merlin & bank payments"}
                >
                  {matchMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                  {remittance.matchAttemptedAt ? "Re-match" : "Match"}
                </button>
              </>
            )}
            <button onClick={onDelete} className="text-red-500 hover:text-red-700 p-1">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-slate-50/50">
          <td colSpan={9} className="px-6 py-4">
            {remittance.parseNotes && (
              <div className="mb-3 text-xs px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                <strong>Parser notes:</strong> {remittance.parseNotes}
              </div>
            )}
            {(remittance.matchNotes || lastMatch) && (
              <div className={`mb-3 text-xs px-3 py-2 rounded-lg border ${
                remittance.status === "matched" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : remittance.status === "needs_review" ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-slate-50 border-slate-200 text-duffells-grey/70"
              }`}>
                <strong className="inline-flex items-center gap-1">
                  {remittance.status === "matched" ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  Matcher:
                </strong>{" "}
                {lastMatch ? lastMatch.notes.join("; ") : remittance.matchNotes}
              </div>
            )}
            {hasHandwritten && remittance.status !== "matched" && (
              <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Handwritten statement — assisted review
                </div>
                <p className="mt-1 text-xs text-amber-800">
                  A handwritten payment of{" "}
                  <strong>{fmtMoney(remittance.handwrittenPayAmount ?? 0)}</strong> was read off this
                  scanned statement. The lines below are the full statement balance — not the amount
                  being paid. Review the suggested oldest-first allocation and accept it to apply the
                  payment to the oldest invoices first. Nothing is posted until you Match and Post.
                </p>
                {suggestionLoading ? (
                  <div className="mt-3 flex items-center gap-2 text-xs text-amber-800">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Building suggestion…
                  </div>
                ) : !suggestion?.available ? (
                  <div className="mt-3 text-xs text-amber-800">
                    {suggestion?.reason || "No suggestion available."}
                  </div>
                ) : (() => {
                  const handwritten = remittance.handwrittenPayAmount ?? suggestion.allocation!.amount;
                  // Full outstanding set on the account (superset of the
                  // suggested lines). The suggested ones carry a default applied
                  // amount; the rest default to their full balance, unselected.
                  const suggestedApplied = new Map(
                    (suggestion.allocation?.lines ?? []).map((l) => [l.ref, l.applied]),
                  );
                  const outstanding = suggestion.outstanding ?? [];
                  const defaultFor = (inv: { ref: string; balance: number }) => {
                    const sa = suggestedApplied.get(inv.ref);
                    return sa != null
                      ? { selected: true, applied: sa.toFixed(2) }
                      : { selected: false, applied: inv.balance.toFixed(2) };
                  };
                  // Sign-aware line validity: invoices (positive balance) take a
                  // positive applied amount within their balance; credit notes
                  // (negative balance) take a negative applied amount whose
                  // magnitude doesn't exceed the credit balance.
                  const lineInvalid = (applied: number, balance: number) => {
                    if (!Number.isFinite(applied)) return true;
                    return balance < 0
                      ? applied >= 0 || applied - balance < -0.01
                      : applied <= 0 || applied - balance > 0.01;
                  };
                  const selectedLines = outstanding
                    .map((inv) => {
                      const e = edits[inv.ref] ?? defaultFor(inv);
                      const applied = parseFloat(e.applied);
                      return { ref: inv.ref, balance: inv.balance, applied, selected: e.selected };
                    })
                    .filter((l) => l.selected);
                  const anyInvalid = selectedLines.some((l) => lineInvalid(l.applied, l.balance));
                  const allocatedTotal = selectedLines.reduce(
                    (s, l) => s + (Number.isFinite(l.applied) ? l.applied : 0),
                    0,
                  );
                  const remainder = handwritten - allocatedTotal;
                  const overAllocated = allocatedTotal - handwritten > 0.01;
                  // A net non-positive allocation is nonsense (a credit note can
                  // only offset invoices, not stand alone) — server rejects it too.
                  const nonPositiveNet = selectedLines.length > 0 && allocatedTotal <= 0.01;
                  const canAccept =
                    selectedLines.length > 0 && !anyInvalid && !overAllocated && !nonPositiveNet;
                  return (
                  <div className="mt-3">
                    <div className="text-xs text-amber-800 mb-1">
                      Outstanding items on account{" "}
                      <span className="font-mono font-semibold">{suggestion.account}</span>{" "}
                      <span className="text-amber-700/70">— the suggested oldest-first allocation is ticked; deselect, edit applied amounts, tick another invoice to add it, or tick a credit note to net it off before accepting:</span>
                    </div>
                    <table className="w-full text-xs bg-white rounded border border-amber-200">
                      <thead className="text-amber-700/70 uppercase">
                        <tr>
                          <th className="px-3 py-1.5 text-center w-8"></th>
                          <th className="px-3 py-1.5 text-left">Invoice</th>
                          <th className="px-3 py-1.5 text-right">Balance</th>
                          <th className="px-3 py-1.5 text-right">Applied</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-100">
                        {outstanding.map((inv) => {
                          const e = edits[inv.ref] ?? defaultFor(inv);
                          const suggested = suggestedApplied.has(inv.ref);
                          const isCredit = inv.balance < 0;
                          const appliedNum = parseFloat(e.applied);
                          const invalid = e.selected && lineInvalid(appliedNum, inv.balance);
                          const partial = Number.isFinite(appliedNum) && Math.abs(appliedNum - inv.balance) > 0.01;
                          return (
                            <tr key={inv.ref} className={e.selected ? "" : "opacity-40"}>
                              <td className="px-3 py-1.5 text-center">
                                <input
                                  type="checkbox"
                                  checked={e.selected}
                                  onClick={(ev) => ev.stopPropagation()}
                                  onChange={(ev) =>
                                    setEdits((prev) => ({
                                      ...prev,
                                      [inv.ref]: { selected: ev.target.checked, applied: (prev[inv.ref] ?? e).applied },
                                    }))
                                  }
                                  className="accent-amber-600"
                                />
                              </td>
                              <td className="px-3 py-1.5 font-mono text-duffells-grey">
                                {inv.ref}
                                {isCredit && (
                                  <span className="ml-1.5 text-[10px] font-sans font-medium uppercase tracking-wide text-sky-600/80">credit note</span>
                                )}
                                {!suggested && !isCredit && (
                                  <span className="ml-1.5 text-[10px] font-sans font-medium uppercase tracking-wide text-amber-600/80">added</span>
                                )}
                              </td>
                              <td className="px-3 py-1.5 text-right text-duffells-grey/70">{fmtMoney(inv.balance)}</td>
                              <td className="px-3 py-1.5 text-right">
                                <input
                                  type="number"
                                  step="0.01"
                                  min={isCredit ? inv.balance : 0}
                                  max={isCredit ? 0 : inv.balance}
                                  value={e.applied}
                                  disabled={!e.selected}
                                  onClick={(ev) => ev.stopPropagation()}
                                  onChange={(ev) =>
                                    setEdits((prev) => ({
                                      ...prev,
                                      [inv.ref]: { selected: (prev[inv.ref] ?? e).selected, applied: ev.target.value },
                                    }))
                                  }
                                  className={`w-24 text-right rounded border px-1.5 py-0.5 font-medium disabled:bg-slate-100 disabled:text-slate-400 ${
                                    invalid
                                      ? "border-red-400 text-red-600"
                                      : partial
                                        ? "border-amber-300 text-amber-700"
                                        : "border-amber-200 text-emerald-700"
                                  }`}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-amber-200 font-semibold">
                          <td></td>
                          <td className="px-3 py-1.5 text-right text-duffells-grey/70">Allocated</td>
                          <td></td>
                          <td className={`px-3 py-1.5 text-right ${overAllocated ? "text-red-600" : "text-duffells-grey"}`}>{fmtMoney(allocatedTotal)}</td>
                        </tr>
                        {remainder > 0.01 && (
                          <tr className="text-amber-700">
                            <td></td>
                            <td className="px-3 py-1.5 text-right">Unallocated (over-payment)</td>
                            <td></td>
                            <td className="px-3 py-1.5 text-right font-medium">{fmtMoney(remainder)}</td>
                          </tr>
                        )}
                      </tfoot>
                    </table>
                    {overAllocated && (
                      <p className="mt-1.5 text-xs text-red-600">
                        Allocated total exceeds the handwritten amount of {fmtMoney(handwritten)}.
                      </p>
                    )}
                    {anyInvalid && !overAllocated && (
                      <p className="mt-1.5 text-xs text-red-600">
                        One or more applied amounts are invalid — invoices must be positive and within their balance; credit notes must be negative and within their credit balance.
                      </p>
                    )}
                    {nonPositiveNet && !anyInvalid && !overAllocated && (
                      <p className="mt-1.5 text-xs text-red-600">
                        The allocation must net to a positive amount — a credit note can only offset invoices, not be allocated on its own.
                      </p>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        acceptSuggestionMutation.mutate(
                          selectedLines.map((l) => ({ ref: l.ref, applied: l.applied })),
                        );
                      }}
                      disabled={acceptSuggestionMutation.isPending || !canAccept}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 px-3 py-1.5 rounded"
                    >
                      {acceptSuggestionMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                      Accept allocation
                    </button>
                  </div>
                  );
                })()}
              </div>
            )}
            {(remittance.sourceFormat === "csv" || remittance.sourceFormat === "xlsx") && (
              <ColumnPickerPanel
                remittanceId={remittance.id}
                isMatched={remittance.status === "matched"}
                onReparsed={() => queryClient.invalidateQueries({ queryKey: ["cash-receipts"] })}
              />
            )}
            {remittance.sourceFormat !== "pdf-vision" && remittance.sourceFormat !== "image" && (
              <ParseProfilePanel
                remittance={remittance}
                onReparsed={() => queryClient.invalidateQueries({ queryKey: ["cash-receipts"] })}
              />
            )}
            {/* Postcode lookup — shown when the remittance is still unmatched and we
                couldn't resolve a Merlin account from the invoice lines. Lets the
                operator find the customer by postcode extracted from the document
                and send them a query email asking which invoices they meant to pay. */}
            {!remittance.archivedAt && remittance.status !== "matched" && !remittance.resolvedAccount && (
              <PostcodeMatchPanel remittance={remittance} />
            )}
            {isLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>
            ) : isError ? (
              <div className="text-sm px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700">
                {(error as Error)?.message || "Failed to load detail"}
              </div>
            ) : !detail || detail.lines.length === 0 ? (
              <div className="text-sm text-duffells-grey/60 py-2">No lines parsed from this file.</div>
            ) : (
              <>
                {(() => {
                  // Per-account breakdown: when a remittance settles invoices on
                  // more than one Merlin account, show each account's line count
                  // and subtotal so the operator can see exactly how the single
                  // BACS payment will be split across accounts before posting.
                  const byAccount = new Map<string, { count: number; sum: number }>();
                  for (const l of detail.lines) {
                    if (!l.merlinCheckedAt || l.merlinNotFound || !l.merlinAccount) continue;
                    const g = byAccount.get(l.merlinAccount) ?? { count: 0, sum: 0 };
                    g.count += 1;
                    g.sum += l.amount;
                    byAccount.set(l.merlinAccount, g);
                  }
                  if (byAccount.size < 2) return null;
                  return (
                    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                      <div className="text-xs font-semibold uppercase text-amber-800 mb-1">
                        Splits across {byAccount.size} accounts
                      </div>
                      <div className="space-y-0.5">
                        {Array.from(byAccount.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([acct, g]) => (
                          <div key={acct} className="flex justify-between text-xs text-duffells-grey">
                            <span className="font-mono text-emerald-700">{acct} <span className="text-duffells-grey/50">({g.count} line{g.count === 1 ? "" : "s"})</span></span>
                            <span className="font-medium">{fmtMoney(g.sum)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-1 text-[11px] text-amber-700">
                        Each invoice is booked to its own account under the shared BACS reference.
                      </div>
                    </div>
                  );
                })()}
                {isMultiAccount && unresolvedLines.length > 0 && (
                  <div className="mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2">
                    <div className="text-xs font-semibold uppercase text-red-800 mb-1 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {unresolvedLines.length} line{unresolvedLines.length === 1 ? "" : "s"} not resolved to an account
                    </div>
                    <div className="text-[11px] text-red-700 mb-1.5">
                      This remittance posts across multiple accounts, so every line must resolve to its own
                      Merlin account before it can be posted. Set the account directly on the line{unresolvedLines.length === 1 ? "" : "s"} below
                      (or re-run Match / fix the reference) first — posting will be rejected otherwise.
                    </div>
                    <div className="space-y-0.5">
                      {unresolvedLines.map((l) => (
                        <div key={l.id} className="flex justify-between text-xs text-red-800">
                          <span className="font-mono">
                            {l.reference}{" "}
                            <span className="text-red-600/70">({!l.merlinCheckedAt ? "not checked" : "not found"})</span>
                          </span>
                          <span className="font-medium">{fmtMoney(l.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-duffells-grey/60">
                    <tr>
                      <th className="px-3 py-2 text-left">Reference</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Doc date</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-left">Merlin account</th>
                      <th className="px-3 py-2 text-right">Merlin invoice total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {detail.lines.map((l) => {
                      const checked = !!l.merlinCheckedAt;
                      const found = checked && !l.merlinNotFound;
                      const amountMismatch = found && l.merlinInvoiceTotal != null && Math.abs(Math.abs(l.amount) - Math.abs(l.merlinInvoiceTotal)) > 0.01;
                      // On a multi-account remittance a line with no resolved
                      // account blocks the whole post — flag it distinctly so the
                      // operator fixes it here rather than failing at Post time.
                      const flagUnresolved = isMultiAccount && (!checked || !found || !l.merlinAccount);
                      return (
                        <tr key={l.id} className={flagUnresolved ? "bg-red-50" : undefined}>
                          <td className="px-3 py-2 text-xs text-duffells-grey">
                            <div className="relative flex flex-col items-start gap-0.5">
                              <div className="inline-flex items-center gap-2">
                                <LineReferenceCell
                                  line={l}
                                  disabled={referenceMutation.isPending}
                                  locked={accountsLocked}
                                  onSave={(reference) => referenceMutation.mutate({ lineId: l.id, reference })}
                                />
                                {!accountsLocked && checked && l.merlinNotFound ? (
                                  <InvoiceMatchPicker
                                    remittanceId={remittance.id}
                                    line={l}
                                    disabled={matchInvoiceMutation.isPending}
                                    onMatched={(reference) => matchInvoiceMutation.mutate({ lineId: l.id, reference })}
                                  />
                                ) : null}
                              {/* Pay-unallocated escape hatch: only for lines a
                                  partial post routed to review (merlin_unmatched /
                                  reference_not_found) on a linked, unlocked payment.
                                  Books this line's cash on-account so it leaves the
                                  review set without editing the ref. */}
                                {!accountsLocked
                                  && remittance.matchedBankPaymentId != null
                                  && (l.merlinAccountWarningCode === "merlin_unmatched"
                                    || l.merlinAccountWarningCode === "reference_not_found") ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!confirm(
                                        `Book ${l.reference} (${fmtMoney(l.amount)}) ON-ACCOUNT as unallocated cash? ` +
                                        `This posts a CashReceipt to Merlin and cannot be undone there.`,
                                      )) return;
                                      payUnallocatedMutation.mutate({ lineId: l.id });
                                    }}
                                    disabled={payUnallocatedMutation.isPending}
                                    className="text-[10px] text-amber-700 hover:text-amber-900 underline disabled:opacity-50 whitespace-nowrap"
                                    title="Post this line's amount on-account (unallocated) and remove it from the review set"
                                  >
                                    {payUnallocatedMutation.isPending && payUnallocatedMutation.variables?.lineId === l.id
                                      ? "posting…"
                                      : "pay unallocated"}
                                  </button>
                                ) : null}
                              </div>
                              <InferredReferenceHint
                                reference={l.reference}
                                inferredReference={l.inferredReference}
                              />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-duffells-grey/70 capitalize">{l.referenceType}</td>
                          <td className="px-3 py-2 text-duffells-grey/70 whitespace-nowrap">{fmtDate(l.documentDate)}</td>
                          <td className={`px-3 py-2 text-right font-medium whitespace-nowrap ${l.amount < 0 ? "text-red-600" : "text-duffells-grey"}`}>
                            <div className="flex flex-col items-end">
                              <span>{fmtMoney(l.amount)}</span>
                              {amountMismatch && (
                                <MerlinAmountCorrectionButton
                                  line={l}
                                  locked={accountsLocked}
                                  disabled={amountCorrectionMutation.isPending}
                                  onUse={() => amountCorrectionMutation.mutate({ lineId: l.id })}
                                />
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">
                            <LineAccountCell
                              line={l}
                              remittanceId={remittance.id}
                              flagUnresolved={flagUnresolved}
                              disabled={lineAccountMutation.isPending}
                              locked={accountsLocked}
                              onSave={(account) => lineAccountMutation.mutate({ lineId: l.id, account })}
                              onRecheck={() => recheckMutation.mutate({ lineId: l.id })}
                              rechecking={recheckingLineId === l.id}
                            />
                          </td>
                          <td className={`px-3 py-2 text-right whitespace-nowrap ${amountMismatch ? "text-red-600 font-medium" : "text-duffells-grey/70"}`}>
                            {l.merlinInvoiceTotal != null ? fmtMoney(l.merlinInvoiceTotal) : <span className="text-slate-400">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 font-semibold">
                      <td colSpan={3} className="px-3 py-2 text-right text-duffells-grey/70">Sum of lines</td>
                      <td className={`px-3 py-2 text-right whitespace-nowrap ${mismatch ? "text-red-600" : "text-duffells-grey"}`}>
                        {fmtMoney(lineSum)}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                    {remittance.totalAmount != null && (
                      <tr className="text-duffells-grey/70">
                        <td colSpan={3} className="px-3 py-2 text-right">Stated total</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">{fmtMoney(remittance.totalAmount)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    )}
                  </tfoot>
                </table>
                {mismatch && (
                  <div className="mt-3 text-xs px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700">
                    Sum of lines ({fmtMoney(lineSum)}) does not match stated total ({fmtMoney(total)}). Difference: {fmtMoney(lineSum - total)}.
                  </div>
                )}
                {lastMatch && lastMatch.candidates.length > 0 && remittance.status !== "matched" && (
                  <div className="mt-4">
                    <h3 className="text-xs uppercase text-duffells-grey/70 font-semibold mb-2">Bank payment candidates</h3>
                    <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                      <thead className="bg-slate-100 text-xs uppercase text-duffells-grey/70">
                        <tr>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                          <th className="px-3 py-2 text-left">Narrative</th>
                          <th className="px-3 py-2 text-left">Why</th>
                          <th className="px-3 py-2 text-right">Score</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {lastMatch.candidates.map((c) => (
                          <tr key={c.bankPaymentId}>
                            <td className="px-3 py-2 whitespace-nowrap">{fmtDate(c.paymentDate)}</td>
                            <td className="px-3 py-2 text-right font-medium whitespace-nowrap">{fmtMoney(c.amount)}</td>
                            <td className="px-3 py-2 font-mono text-xs text-duffells-grey/70 max-w-[280px] truncate">{c.narrative || <span className="text-slate-400">(empty)</span>}</td>
                            <td className="px-3 py-2 text-xs text-duffells-grey/70">{c.reasons.join(", ")}</td>
                            <td className="px-3 py-2 text-right text-xs font-semibold">{c.score}</td>
                            <td className="px-3 py-2 text-right">
                              <button
                                onClick={() => confirmMutation.mutate(c.bankPaymentId)}
                                disabled={confirmMutation.isPending}
                                className="text-xs text-primary hover:underline font-medium"
                              >
                                Confirm
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ===== Stripe tab =====

interface StripeTransaction {
  id: number;
  stripeId: string;
  amount: string;
  currency: string;
  description: string;
  orderNumber: string | null;
  stripeCreated: string;
  stripeStatus: string;
  fee: string;
  net: string;
  matched: boolean;
  merlinAccount: string | null;
  merlinInvoice: string | null;
  merlinSltranid: string | null;
  merlinCustomerid: string | null;
  merlinInvoiceTotal: string | null;
  merlinPaymentAmount: string | null;
  cashReceiptSubmitted: boolean;
  cashReceiptAt: string | null;
  cashReceiptBy: string | null;
  cashReceiptResultCode: string | null;
  cashReceiptResultMessage: string | null;
  cashReceiptReference: string | null;
}

interface StripePartInvoicedOrder {
  id: number;
  stripeId: string;
  webref2: string;
  account: string;
  customerid: string;
  originalInvoice: string;
  originalSltranid: string;
  invoicedValue: string;
  orderBalance: string;
  unallocatedAmount: string;
  stripeAmount: string;
  unallocatedReference: string;
  unallocatedSltranid: string | null;
  unallocatedPostedAt: string;
  status: string;
  newInvoice: string | null;
  newSltranid: string | null;
  reallocatedAt: string | null;
  reallocationReference: string | null;
  lastCheckedAt: string | null;
  resultCode: string | null;
  resultMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

interface StripeGroup {
  stripeId: string;
  rows: StripeTransaction[];
  totalInvoice: number;
  amount: number;
  net: number;
  created: string;
  orderNumber: string | null;
  matched: boolean;
  posted: boolean;
  anyFailed: boolean;
  anySubmittedNoResult: boolean;
  description: string;
}

function groupStripeRows(rows: StripeTransaction[]): StripeGroup[] {
  const byId = new Map<string, StripeTransaction[]>();
  for (const r of rows) {
    if (!byId.has(r.stripeId)) byId.set(r.stripeId, []);
    byId.get(r.stripeId)!.push(r);
  }
  const out: StripeGroup[] = [];
  for (const [stripeId, list] of byId.entries()) {
    const first = list[0];
    out.push({
      stripeId,
      rows: list,
      totalInvoice: list.reduce((s, r) => s + Number(r.merlinInvoiceTotal ?? 0), 0),
      amount: Number(first.amount),
      net: Number(first.net),
      created: first.stripeCreated,
      orderNumber: first.orderNumber,
      matched: list.every((r) => r.matched),
      posted: list.every((r) => r.cashReceiptSubmitted && r.cashReceiptResultCode === "0"),
      anyFailed: list.some((r) => r.cashReceiptSubmitted && r.cashReceiptResultCode && r.cashReceiptResultCode !== "0"),
      anySubmittedNoResult: list.some((r) => r.cashReceiptSubmitted && r.cashReceiptResultCode == null),
      description: first.description,
    });
  }
  out.sort((a, b) => b.created.localeCompare(a.created));
  return out;
}

function StripeTab({ isAdmin }: { isAdmin: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<"all" | "unmatched" | "ready" | "posted" | "failed">("ready");

  const { data: transactions = [], isLoading } = useQuery<StripeTransaction[]>({
    queryKey: ["stripe-transactions"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/stripe-transactions`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load Stripe transactions");
      const data = await res.json();
      return data.transactions ?? [];
    },
  });

  const { data: dailyFees } = useQuery<{ days: number; rows: Array<{ day: string; stripeFee: number; networkCost: number; total: number; entries: number }> }>({
    queryKey: ["stripe-daily-fees"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/stripe-transactions/daily-fees?days=30`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load Stripe fees");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: partInvoiced = [] } = useQuery<StripePartInvoicedOrder[]>({
    queryKey: ["stripe-part-invoiced"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/stripe-transactions/part-invoiced`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load part-invoiced orders");
      const data = await res.json();
      return data.orders ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      // The Datahub backfill is bounded server-side to stay under the gateway
      // timeout, so a large backlog is cleared over several quick rounds. Loop
      // automatically until the server reports it has caught up (or hits a
      // safety cap), refreshing the table after each round so progress shows.
      let last: any = null;
      let rounds = 0;
      let totalInserted = 0;
      let totalRematched = 0;
      const MAX_ROUNDS = 50;
      for (let i = 0; i < MAX_ROUNDS; i++) {
        const res = await fetch(`${BASE}/api/stripe-transactions/sync`, {
          method: "POST",
          credentials: "include",
          headers: { "X-Requested-With": "fetch" },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.ok) throw new Error(data?.error || "Sync failed");
        last = data;
        rounds++;
        totalInserted += data.insertedRows ?? 0;
        totalRematched += data.rematched ?? 0;
        queryClient.invalidateQueries({ queryKey: ["stripe-transactions"] });
        if (data.caughtUp) break;
      }
      return { ...last, rounds, totalInserted, totalRematched };
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["stripe-transactions"] });
      toast({
        title: data.caughtUp ? "Stripe sync complete" : "Stripe sync paused",
        description:
          `${data.rounds} round(s). Added ${data.totalInserted}, rematched ${data.totalRematched}. ` +
          `Matched ${data.matchedCount}, unmatched ${data.unmatchedCount}.` +
          (data.caughtUp ? "" : " More days remain — click Sync again to continue."),
      });
    },
    onError: (err: any) => toast({ title: "Stripe sync failed", description: err?.message, variant: "destructive" }),
  });

  const postMutation = useMutation({
    mutationFn: async (stripeId: string) => {
      const res = await fetch(`${BASE}/api/stripe-transactions/${encodeURIComponent(stripeId)}/post-to-merlin`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Posting failed");
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["stripe-transactions"] });
      if (data.allOk) {
        toast({ title: `Posted ${data.succeeded}/${data.attempted} invoice(s) to Merlin` });
      } else {
        toast({
          title: `Partial post: ${data.succeeded}/${data.attempted} succeeded`,
          description: data.submissions.filter((s: any) => s.code !== "0").map((s: any) => `${s.account ? `[${s.account}] ` : ""}${s.invoiceRef}: ${s.message}`).join("; "),
          variant: "destructive",
        });
      }
    },
    onError: (err: any) => toast({ title: "Posting failed", description: err?.message, variant: "destructive" }),
  });

  const postOnAccountMutation = useMutation({
    mutationFn: async (stripeId: string) => {
      const res = await fetch(`${BASE}/api/stripe-transactions/${encodeURIComponent(stripeId)}/post-on-account`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || "On-account posting failed");
      return data as { ok: true; result: { amount: number; account: string; reference: string } };
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["stripe-transactions"] });
      void queryClient.invalidateQueries({ queryKey: ["stripe-part-invoiced"] });
      toast({
        title: "Posted on account",
        description: `${fmtMoney(data.result.amount)} posted to ${data.result.account}; Merlin reference ${data.result.reference}.`,
      });
    },
    onError: (err: any) => toast({ title: "On-account posting failed", description: err?.message, variant: "destructive" }),
  });

  const resetMutation = useMutation({
    mutationFn: async (stripeId: string) => {
      const res = await fetch(`${BASE}/api/stripe-transactions/${encodeURIComponent(stripeId)}/reset-merlin-posting`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Reset failed");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stripe-transactions"] });
      toast({ title: "Merlin posting state reset" });
    },
    onError: (err: any) => toast({ title: "Reset failed", description: err?.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (stripeId: string) => {
      const res = await fetch(`${BASE}/api/stripe-transactions/${encodeURIComponent(stripeId)}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Delete failed");
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["stripe-transactions"] });
      toast({ title: `Deleted ${data.deleted} row(s)` });
    },
    onError: (err: any) => toast({ title: "Delete failed", description: err?.message, variant: "destructive" }),
  });

  const clearAllMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/stripe-transactions`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({ confirm: "DELETE ALL" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Clear all failed");
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["stripe-transactions"] });
      toast({ title: `Cleared ${data.deleted} row(s) from stripe_transactions` });
    },
    onError: (err: any) => toast({ title: "Clear all failed", description: err?.message, variant: "destructive" }),
  });

  const deleteDayMutation = useMutation({
    mutationFn: async (date: string) => {
      const res = await fetch(`${BASE}/api/stripe-transactions/by-day/${encodeURIComponent(date)}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "X-Requested-With": "fetch" },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Delete day failed");
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["stripe-transactions"] });
      toast({ title: `Deleted ${data.deleted} row(s) across ${data.charges} charge(s) for ${data.date}` });
    },
    onError: (err: any) => toast({ title: "Delete day failed", description: err?.message, variant: "destructive" }),
  });

  const [deleteDay, setDeleteDay] = useState("");
  // Days we can offer for whole-day deletion, bucketed by UTC day to
  // match the server. The server refuses the ENTIRE day if any row that
  // day is protected (posted to Merlin or awaiting a result), so a day
  // is only offered when EVERY row in it is deletable.
  const dayOptions = (() => {
    const byDay = new Map<string, { count: number; hasProtected: boolean }>();
    for (const t of transactions) {
      const protectedRow =
        t.cashReceiptResultCode === "0" ||
        (t.cashReceiptSubmitted && t.cashReceiptResultCode == null);
      const day = new Date(t.stripeCreated).toISOString().slice(0, 10);
      const entry = byDay.get(day) ?? { count: 0, hasProtected: false };
      entry.count += 1;
      if (protectedRow) entry.hasProtected = true;
      byDay.set(day, entry);
    }
    return Array.from(byDay.entries())
      .filter(([, v]) => !v.hasProtected)
      .map(([day, v]) => ({ day, count: v.count }))
      .sort((a, b) => b.day.localeCompare(a.day));
  })();

  const groups = groupStripeRows(transactions);
  const filtered = groups.filter((g) => {
    if (filter === "all") return true;
    if (filter === "unmatched") return !g.matched;
    if (filter === "posted") return g.posted;
    if (filter === "failed") return g.anyFailed || g.anySubmittedNoResult;
    // ready: matched + not submitted + all unpaid in Merlin
    return g.matched && g.rows.every((r) => !r.cashReceiptSubmitted && Number(r.merlinPaymentAmount ?? 0) < 0.005);
  });

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-semibold text-duffells-grey flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Stripe transactions
          </h2>
          <p className="text-xs text-duffells-grey/60 mt-1">
            Synced daily at 23:00 UTC. Matched to Merlin invoices by <code>#orderNumber</code> in the Stripe description.
            {isAdmin && " Click Sync to refresh on demand."}
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-2">
              <Button onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending} size="sm" variant="outline">
                {syncMutation.isPending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                Sync now
              </Button>
              <Button
                onClick={() => {
                  const charges = groups.length;
                  const rows = transactions.length;
                  if (!confirm(`Delete ALL ${rows} row(s) across ${charges} Stripe charge(s) from the database? This cannot be undone. (Will be refused if any row was successfully posted to Merlin or is awaiting a Merlin result.)`)) return;
                  clearAllMutation.mutate();
                }}
                disabled={clearAllMutation.isPending || groups.length === 0}
                size="sm"
                variant="outline"
                className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-700"
              >
                {clearAllMutation.isPending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
                Clear all
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={deleteDay}
                onChange={(e) => setDeleteDay(e.target.value)}
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-duffells-grey/80 max-w-[180px]"
              >
                <option value="">Delete a day…</option>
                {dayOptions.map((d) => (
                  <option key={d.day} value={d.day}>
                    {d.day} ({d.count} row{d.count === 1 ? "" : "s"})
                  </option>
                ))}
              </select>
              <Button
                onClick={() => {
                  const opt = dayOptions.find((d) => d.day === deleteDay);
                  if (!opt) return;
                  if (!confirm(`Delete all ${opt.count} Stripe row(s) created on ${opt.day} from the database? This cannot be undone. (Will be refused if any row that day was successfully posted to Merlin or is awaiting a Merlin result.)`)) return;
                  deleteDayMutation.mutate(opt.day);
                }}
                disabled={deleteDayMutation.isPending || !deleteDay}
                size="sm"
                variant="outline"
                className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-700"
              >
                {deleteDayMutation.isPending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
                Delete day
              </Button>
            </div>
          </div>
        )}
      </div>

      {partInvoiced.length > 0 && (
        <StripePartInvoicedPanel orders={partInvoiced} />
      )}

      {dailyFees && dailyFees.rows.length > 0 && (
        <StripeDailyFeesPanel rows={dailyFees.rows} />
      )}

      <div className="flex gap-1">
        {(["ready", "unmatched", "failed", "posted", "all"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs rounded-full border ${
              filter === f
                ? "bg-primary text-white border-primary"
                : "bg-white border-slate-200 text-duffells-grey/70 hover:border-slate-300"
            }`}
          >
            {f === "ready" ? "Ready to post" : f.charAt(0).toUpperCase() + f.slice(1)}
            <span className="ml-1.5 opacity-60">
              {(f === "all"
                ? groups
                : f === "unmatched"
                ? groups.filter((g) => !g.matched)
                : f === "posted"
                ? groups.filter((g) => g.posted)
                : f === "failed"
                ? groups.filter((g) => g.anyFailed || g.anySubmittedNoResult)
                : groups.filter((g) => g.matched && g.rows.every((r) => !r.cashReceiptSubmitted && Number(r.merlinPaymentAmount ?? 0) < 0.005))
              ).length}
            </span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-duffells-grey/60">
            <Loader2 className="w-6 h-6 mx-auto animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-duffells-grey/60 text-sm">No Stripe transactions in this view.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/60">
              <tr>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Order #</th>
                <th className="text-left px-4 py-2">Description</th>
                <th className="text-right px-4 py-2">Stripe £</th>
                <th className="text-left px-4 py-2">Invoices</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => (
                <StripeGroupRow
                  key={g.stripeId}
                  group={g}
                  isAdmin={isAdmin}
                  onPost={() => postMutation.mutate(g.stripeId)}
                  onPostOnAccount={() => {
                    if (!confirm(
                      `Post ${fmtMoney(g.amount)} for Stripe order #${g.orderNumber} on account? ` +
                      "It will remain as unallocated cash until the invoice appears in Merlin.",
                    )) return;
                    postOnAccountMutation.mutate(g.stripeId);
                  }}
                  onReset={() => resetMutation.mutate(g.stripeId)}
                  onDelete={() => {
                    const what = g.rows.length === 1 ? "1 row" : `${g.rows.length} sibling rows`;
                    if (!confirm(`Delete ${what} for Stripe charge ${g.stripeId} (${g.orderNumber ? `#${g.orderNumber}` : "no order#"})? This cannot be undone.`)) return;
                    deleteMutation.mutate(g.stripeId);
                  }}
                  posting={postMutation.isPending && postMutation.variables === g.stripeId}
                  postingOnAccount={postOnAccountMutation.isPending && postOnAccountMutation.variables === g.stripeId}
                  resetting={resetMutation.isPending && resetMutation.variables === g.stripeId}
                  deleting={deleteMutation.isPending && deleteMutation.variables === g.stripeId}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StripePartInvoicedPanel({ orders }: { orders: StripePartInvoicedOrder[] }) {
  const [expanded, setExpanded] = useState(true);
  const queryClient = useQueryClient();
  const [pendingId, setPendingId] = useState<number | null>(null);
  const reallocateMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/stripe-transactions/part-invoiced/${id}/reallocate`, {
        method: "POST",
        credentials: "include",
        headers: { "x-requested-with": "XMLHttpRequest" },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Reallocation failed");
      return data;
    },
    onSettled: () => {
      setPendingId(null);
      void queryClient.invalidateQueries({ queryKey: ["stripe-part-invoiced"] });
    },
    onError: (err: unknown) => {
      window.alert(err instanceof Error ? err.message : "Reallocation failed");
    },
  });
  const fmt = (v: string | number | null | undefined) =>
    `£${Number(v ?? 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleDateString("en-GB") : "—");
  const open = orders.filter((o) => o.status === "awaiting_invoice" || o.status === "overdue");
  const overdue = orders.filter((o) => o.status === "overdue");
  const totalParked = open.reduce((s, o) => s + Number(o.unallocatedAmount ?? 0), 0);
  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      awaiting_invoice: "bg-amber-50 text-amber-700 border-amber-200",
      overdue: "bg-red-50 text-red-700 border-red-200",
      reallocated: "bg-emerald-50 text-emerald-700 border-emerald-200",
      error: "bg-red-50 text-red-700 border-red-200",
    };
    const label: Record<string, string> = {
      awaiting_invoice: "Awaiting invoice",
      overdue: "Overdue (>28d)",
      reallocated: "Reallocated",
      error: "Error",
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full border ${map[status] ?? "bg-slate-50 text-slate-600 border-slate-200"}`}>
        {label[status] ?? status}
      </span>
    );
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-6 py-3 flex items-center justify-between hover:bg-slate-50/50 transition-colors"
      >
        <div className="flex items-center gap-3 text-sm">
          {expanded ? <ChevronDown className="w-4 h-4 text-duffells-grey/60" /> : <ChevronRight className="w-4 h-4 text-duffells-grey/60" />}
          <span className="font-medium text-duffells-grey">Part-invoiced orders (Stripe overpayments on-account)</span>
          {overdue.length > 0 && (
            <span className="px-2 py-0.5 text-xs rounded-full border bg-red-50 text-red-700 border-red-200">
              {overdue.length} overdue
            </span>
          )}
          <span className="text-xs text-duffells-grey/50">
            {open.length} parked · {fmt(totalParked)} awaiting reallocation
          </span>
        </div>
      </button>
      {expanded && (
        <div className="overflow-x-auto border-t border-slate-100">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/60">
              <tr>
                <th className="text-left px-4 py-2">Posted</th>
                <th className="text-left px-4 py-2">Order #</th>
                <th className="text-left px-4 py-2">Account</th>
                <th className="text-left px-4 py-2">Orig. invoice</th>
                <th className="text-right px-4 py-2">Invoiced</th>
                <th className="text-right px-4 py-2">On-account</th>
                <th className="text-right px-4 py-2">Stripe</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">New invoice</th>
                <th className="text-right px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 whitespace-nowrap text-duffells-grey/70">{fmtDate(o.unallocatedPostedAt)}</td>
                  <td className="px-4 py-2 font-medium text-duffells-grey">{o.webref2}</td>
                  <td className="px-4 py-2 text-duffells-grey/70">{o.account}</td>
                  <td className="px-4 py-2 text-duffells-grey/70">{o.originalInvoice}</td>
                  <td className="px-4 py-2 text-right text-duffells-grey/70">{fmt(o.invoicedValue)}</td>
                  <td className="px-4 py-2 text-right font-medium text-duffells-grey">{fmt(o.unallocatedAmount)}</td>
                  <td className="px-4 py-2 text-right text-duffells-grey/70">{fmt(o.stripeAmount)}</td>
                  <td className="px-4 py-2">{statusBadge(o.status)}</td>
                  <td className="px-4 py-2 text-duffells-grey/70">
                    {o.newInvoice ? (
                      <span title={o.reallocatedAt ? `Reallocated ${fmtDate(o.reallocatedAt)}` : undefined}>{o.newInvoice}</span>
                    ) : o.resultMessage ? (
                      <span className="text-red-600 text-xs" title={o.resultMessage}>{o.resultMessage.slice(0, 40)}</span>
                    ) : (
                      <span className="text-duffells-grey/40">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {o.status === "awaiting_invoice" || o.status === "overdue" ? (
                      <button
                        onClick={() => {
                          setPendingId(o.id);
                          reallocateMutation.mutate(o.id);
                        }}
                        disabled={reallocateMutation.isPending}
                        className="px-3 py-1 text-xs rounded-lg border border-duffells-grey/20 text-duffells-grey hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        title="Find the order's final invoice and apply the parked surplus now"
                      >
                        {pendingId === o.id && reallocateMutation.isPending ? "Reallocating…" : "Reallocate"}
                      </button>
                    ) : (
                      <span className="text-duffells-grey/40">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StripeDailyFeesPanel({ rows }: { rows: Array<{ day: string; stripeFee: number; networkCost: number; total: number; entries: number }> }) {
  const [expanded, setExpanded] = useState(false);
  const totalStripe = rows.reduce((s, r) => s + r.stripeFee, 0);
  const totalNetwork = rows.reduce((s, r) => s + r.networkCost, 0);
  const total = totalStripe + totalNetwork;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-6 py-3 flex items-center justify-between hover:bg-slate-50/50 transition-colors"
      >
        <div className="flex items-center gap-3 text-sm">
          {expanded ? <ChevronDown className="w-4 h-4 text-duffells-grey/60" /> : <ChevronRight className="w-4 h-4 text-duffells-grey/60" />}
          <span className="font-medium text-duffells-grey">Stripe fees (last {rows.length} day{rows.length === 1 ? "" : "s"})</span>
          <span className="text-xs text-duffells-grey/50">
            Posted as daily summary BTs, not deducted on individual charges
          </span>
        </div>
        <div className="flex items-center gap-6 text-xs">
          <span className="text-duffells-grey/60">Stripe <span className="font-medium text-duffells-grey ml-1">{fmtMoney(totalStripe)}</span></span>
          <span className="text-duffells-grey/60">Network <span className="font-medium text-duffells-grey ml-1">{fmtMoney(totalNetwork)}</span></span>
          <span className="text-duffells-grey/60">Total <span className="font-semibold text-duffells-grey ml-1">{fmtMoney(total)}</span></span>
        </div>
      </button>
      {expanded && (
        <table className="w-full text-sm border-t border-slate-100">
          <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/60">
            <tr>
              <th className="text-left px-4 py-2">Day</th>
              <th className="text-right px-4 py-2">Stripe fee £</th>
              <th className="text-right px-4 py-2">Network cost £</th>
              <th className="text-right px-4 py-2">Total £</th>
              <th className="text-right px-4 py-2 text-duffells-grey/40">BTs</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.day} className="border-t border-slate-100">
                <td className="px-4 py-2 text-duffells-grey/70">{r.day}</td>
                <td className="px-4 py-2 text-right">{fmtMoney(r.stripeFee)}</td>
                <td className="px-4 py-2 text-right">{fmtMoney(r.networkCost)}</td>
                <td className="px-4 py-2 text-right font-medium">{fmtMoney(r.total)}</td>
                <td className="px-4 py-2 text-right text-duffells-grey/40 text-xs">{r.entries}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StripeGroupRow({
  group,
  isAdmin,
  onPost,
  onPostOnAccount,
  onReset,
  onDelete,
  posting,
  postingOnAccount,
  resetting,
  deleting,
}: {
  group: StripeGroup;
  isAdmin: boolean;
  onPost: () => void;
  onPostOnAccount: () => void;
  onReset: () => void;
  onDelete: () => void;
  posting: boolean;
  postingOnAccount: boolean;
  resetting: boolean;
  deleting: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const status = group.posted
    ? { label: "Posted", icon: <CheckCircle2 className="w-3.5 h-3.5" />, cls: "bg-green-50 text-green-700 border-green-200" }
    : group.anySubmittedNoResult
    ? { label: "Investigate", icon: <AlertCircle className="w-3.5 h-3.5" />, cls: "bg-amber-50 text-amber-700 border-amber-200" }
    : group.anyFailed
    ? { label: "Failed", icon: <AlertCircle className="w-3.5 h-3.5" />, cls: "bg-red-50 text-red-700 border-red-200" }
    : !group.matched
    ? { label: "Unmatched", icon: <Link2Off className="w-3.5 h-3.5" />, cls: "bg-slate-50 text-slate-600 border-slate-200" }
    : group.rows.some((r) => Number(r.merlinPaymentAmount ?? 0) >= 0.005)
    ? { label: "Already paid", icon: <Link2 className="w-3.5 h-3.5" />, cls: "bg-blue-50 text-blue-700 border-blue-200" }
    : { label: "Ready", icon: <PlayCircle className="w-3.5 h-3.5" />, cls: "bg-violet-50 text-violet-700 border-violet-200" };

  const canPost =
    isAdmin &&
    group.matched &&
    !group.posted &&
    !group.anyFailed &&
    !group.anySubmittedNoResult &&
    group.rows.every((r) => !r.cashReceiptSubmitted && Number(r.merlinPaymentAmount ?? 0) < 0.005);

  // No-invoice cash can only be posted after an explicit administrator action
  // while this flow is being tested. Match the server's safety contract:
  // exactly one unmatched settled row, an order number, and no prior submission.
  const canPostOnAccount =
    isAdmin &&
    !group.matched &&
    group.rows.length === 1 &&
    group.orderNumber != null &&
    group.rows[0].stripeStatus === "available" &&
    !group.rows[0].cashReceiptSubmitted;

  const canReset = isAdmin && (group.anyFailed || group.anySubmittedNoResult);
  // Hide delete when any sibling is protected server-side: a successful
  // Merlin post (audit trail) or a submitted-without-result row
  // (unknown outcome, must investigate). Mirrors the server predicate
  // so the button doesn't appear for groups the server will refuse.
  const anyPostedSuccess = group.rows.some((r) => r.cashReceiptResultCode === "0");
  const canDelete = isAdmin && !anyPostedSuccess && !group.anySubmittedNoResult;

  return (
    <>
      <tr className="border-t border-slate-100 hover:bg-slate-50/50">
        <td className="px-4 py-2 text-duffells-grey/70 whitespace-nowrap">{fmtDate(group.created)}</td>
        <td className="px-4 py-2 font-mono text-xs">{group.orderNumber ?? "—"}</td>
        <td className="px-4 py-2 max-w-xs truncate text-duffells-grey/70" title={group.description}>
          {group.description || "—"}
        </td>
        <td className="px-4 py-2 text-right font-medium">{fmtMoney(group.amount)}</td>
        <td className="px-4 py-2">
          {group.matched ? (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {group.rows.length} invoice{group.rows.length === 1 ? "" : "s"} · {fmtMoney(group.totalInvoice)}
            </button>
          ) : (
            <span className="text-xs text-duffells-grey/40">—</span>
          )}
        </td>
        <td className="px-4 py-2">
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${status.cls}`}>
            {status.icon}
            {status.label}
          </span>
        </td>
        <td className="px-4 py-2 text-right whitespace-nowrap">
          {canPost && (
            <Button size="sm" onClick={onPost} disabled={posting}>
              {posting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Banknote className="w-3.5 h-3.5 mr-1" />}
              Post
            </Button>
          )}
          {canPostOnAccount && (
            <Button size="sm" variant="outline" onClick={onPostOnAccount} disabled={postingOnAccount}>
              {postingOnAccount ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Banknote className="w-3.5 h-3.5 mr-1" />}
              Post on account
            </Button>
          )}
          {canReset && (
            <Button size="sm" variant="outline" onClick={onReset} disabled={resetting}>
              {resetting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Reset
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onDelete}
              disabled={deleting}
              className="ml-1 text-red-700 hover:bg-red-50 hover:text-red-700"
              title="Delete this Stripe charge from the database"
            >
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </Button>
          )}
        </td>
      </tr>
      {expanded && group.matched && (
        <tr className="bg-slate-50/50">
          <td colSpan={7} className="px-4 py-3">
            <table className="w-full text-xs">
              <thead className="text-duffells-grey/50 uppercase">
                <tr>
                  <th className="text-left py-1">Account</th>
                  <th className="text-left py-1">Invoice</th>
                  <th className="text-right py-1">Invoice £</th>
                  <th className="text-right py-1">Paid £</th>
                  <th className="text-left py-1">Sltranid</th>
                  <th className="text-left py-1">Result</th>
                </tr>
              </thead>
              <tbody>
                {group.rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-200/60">
                    <td className="py-1 font-mono">{r.merlinAccount ?? "—"}</td>
                    <td className="py-1 font-mono">{r.merlinInvoice ?? "—"}</td>
                    <td className="py-1 text-right">{fmtMoney(Number(r.merlinInvoiceTotal ?? 0))}</td>
                    <td className="py-1 text-right">{fmtMoney(Number(r.merlinPaymentAmount ?? 0))}</td>
                    <td className="py-1 font-mono">{r.merlinSltranid ?? "—"}</td>
                    <td className="py-1">
                      {r.cashReceiptResultCode == null
                        ? r.cashReceiptSubmitted
                          ? <span className="text-amber-600">submitted, no result</span>
                          : <span className="text-duffells-grey/40">—</span>
                        : r.cashReceiptResultCode === "0"
                          ? <span className="text-green-700">OK — {r.cashReceiptReference}</span>
                          : <span className="text-red-700">{r.cashReceiptResultCode}: {r.cashReceiptResultMessage}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

// ---- Credit matching tab -------------------------------------------------
//
// Lists every unallocated credit note in Merlin, paired (when unambiguous)
// with the matching unpaid invoice on the same account whose value is the
// CN's value × -1. One click posts a zero-cash contra to Merlin — both
// rows become fully allocated, no bank movement.
//
// Source of truth is Merlin itself; there's no local table. After every
// post we refetch the list, so freshly-cleared pairs disappear.

interface CreditMatchCandidate {
  account: string;
  customerid: string;
  creditNote: { ref: string; sltranid: string; amount: number };
  invoice: { ref: string; sltranid: string; amount: number } | null;
  ambiguousInvoiceRefs: string[];
  ambiguousInvoices: { ref: string; sltranid: string; amount: number }[];
  status: "ready" | "ambiguous" | "no-match";
}

function CreditMatchingTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [postingKey, setPostingKey] = useState<string | null>(null);
  // For ambiguous rows: which candidate invoice the operator has picked,
  // keyed by the credit note's sltranid.
  const [pickedInvoice, setPickedInvoice] = useState<Record<string, string>>({});

  const { data: matches = [], isLoading, isFetching, refetch, error } = useQuery<CreditMatchCandidate[]>({
    queryKey: ["cash-receipts", "credit-matches"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/credit-matches`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      return res.json();
    },
    // The query hits Merlin — don't auto-refetch on focus, the user can
    // press Refresh explicitly. Stale-while-revalidate for 60s.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Post a contra. For "ready" rows the invoice is m.invoice. For "ambiguous"
  // rows the operator picks one of m.ambiguousInvoices, passed as `chosen`;
  // operatorChosen tells the server to allow a pick among the competing
  // candidates (it still re-validates eligibility before posting).
  const postContra = async (
    m: CreditMatchCandidate,
    chosen?: { ref: string; sltranid: string; amount: number },
  ) => {
    const invoice = chosen ?? m.invoice;
    if (!invoice) return;
    const operatorChosen = Boolean(chosen);
    const key = `${m.creditNote.sltranid}:${invoice.sltranid}`;
    setPostingKey(key);
    try {
      const res = await fetch(`${BASE}/api/cash-receipts/credit-matches/post`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({
          account: m.account,
          invoiceSltranid: invoice.sltranid,
          creditNoteSltranid: m.creditNote.sltranid,
          operatorChosen,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        toast({
          title: "Contra post failed",
          description: data.error || data.message || `Merlin returned code ${data.code ?? "unknown"}`,
          variant: "destructive",
        });
        return;
      }
      toast({
        title: `Posted contra: ${invoice.ref} ↔ ${m.creditNote.ref}`,
        description: `£${invoice.amount.toFixed(2)} on ${m.account} (ref ${data.reference})`,
      });
      queryClient.invalidateQueries({ queryKey: ["cash-receipts", "credit-matches"] });
    } catch (err: any) {
      toast({ title: "Contra post failed", description: err?.message || "Network error", variant: "destructive" });
    } finally {
      setPostingKey(null);
    }
  };

  const ready = matches.filter((m) => m.status === "ready");
  const ambiguous = matches.filter((m) => m.status === "ambiguous");
  const noMatch = matches.filter((m) => m.status === "no-match");

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-semibold text-duffells-grey flex items-center gap-2">
              <ArrowLeftRight className="w-4 h-4" />
              Credit-note ↔ invoice matching
            </h2>
            <p className="text-xs text-duffells-grey/60 mt-1 max-w-3xl">
              Every unallocated credit note in Merlin, paired (when the value matches a single unpaid
              invoice on the same account) with that invoice. Posting a pair sends a zero-value
              CashReceipt with both transactions — no cash moves, both rows become fully allocated.
              Source of truth is Merlin; click Refresh after posting elsewhere to re-sync.
            </p>
          </div>
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Refresh
          </Button>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <AlertCircle className="w-4 h-4 inline mr-1.5" />
            {(error as any).message || "Failed to load credit matches"}
          </div>
        )}

        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-2xl font-semibold text-emerald-700">{ready.length}</div>
            <div className="text-xs text-emerald-700/80">Ready to contra</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="text-2xl font-semibold text-amber-700">{ambiguous.length}</div>
            <div className="text-xs text-amber-700/80">Ambiguous (multiple matches)</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-2xl font-semibold text-slate-700">{noMatch.length}</div>
            <div className="text-xs text-slate-700/80">No matching invoice</div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : matches.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-sm text-duffells-grey/60">
          No unallocated credit notes in Merlin. Nothing to do.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/70">
              <tr>
                <th className="px-4 py-3 text-left">Account</th>
                <th className="px-4 py-3 text-left">Credit note</th>
                <th className="px-4 py-3 text-left">Invoice</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {matches.map((m) => {
                const key = `${m.creditNote.sltranid}:${m.invoice?.sltranid ?? "none"}`;
                const isPosting = postingKey === key;
                // For ambiguous rows: the invoice the operator picked (if any)
                // and whether this row's manual post is in flight.
                const pickedId = pickedInvoice[m.creditNote.sltranid] ?? "";
                const chosenInvoice = m.ambiguousInvoices.find((inv) => inv.sltranid === pickedId);
                const isPostingPicked = chosenInvoice
                  ? postingKey === `${m.creditNote.sltranid}:${chosenInvoice.sltranid}`
                  : false;
                return (
                  <tr key={`${m.account}-${m.creditNote.sltranid}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs text-duffells-grey">{m.account}</td>
                    <td className="px-4 py-3 font-mono text-xs text-red-700">{m.creditNote.ref}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {m.invoice ? (
                        <span className="text-emerald-700">{m.invoice.ref}</span>
                      ) : m.ambiguousInvoices.length > 0 ? (
                        <select
                          className="border border-amber-300 rounded-md px-2 py-1 text-xs font-mono bg-amber-50 text-amber-800 max-w-[16rem]"
                          value={pickedInvoice[m.creditNote.sltranid] ?? ""}
                          disabled={postingKey !== null}
                          onChange={(e) =>
                            setPickedInvoice((prev) => ({ ...prev, [m.creditNote.sltranid]: e.target.value }))
                          }
                          title={`${m.ambiguousInvoices.length} invoices on this account have a balance of £${m.creditNote.amount.toFixed(2)} — pick one to contra`}
                        >
                          <option value="">Pick invoice…</option>
                          {m.ambiguousInvoices.map((inv) => (
                            <option key={inv.sltranid} value={inv.sltranid}>
                              {inv.ref} — £{inv.amount.toFixed(2)}
                            </option>
                          ))}
                        </select>
                      ) : m.ambiguousInvoiceRefs.length > 0 ? (
                        <span className="text-amber-700" title={`Multiple invoices on this account have a balance of £${m.creditNote.amount.toFixed(2)}`}>
                          {m.ambiguousInvoiceRefs.slice(0, 3).join(", ")}
                          {m.ambiguousInvoiceRefs.length > 3 && ` +${m.ambiguousInvoiceRefs.length - 3} more`}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{fmtMoney(m.creditNote.amount)}</td>
                    <td className="px-4 py-3 text-xs">
                      {m.status === "ready" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                          <CheckCircle2 className="w-3 h-3" /> Ready
                        </span>
                      )}
                      {m.status === "ambiguous" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                          <AlertCircle className="w-3 h-3" /> Ambiguous
                        </span>
                      )}
                      {m.status === "no-match" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                          No invoice
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {m.status === "ready" && (
                        <Button
                          size="sm"
                          onClick={() => postContra(m)}
                          disabled={isPosting || postingKey !== null}
                        >
                          {isPosting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <PlayCircle className="w-3 h-3 mr-1" />}
                          Post contra
                        </Button>
                      )}
                      {m.status === "ambiguous" && m.ambiguousInvoices.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => chosenInvoice && postContra(m, chosenInvoice)}
                          disabled={!chosenInvoice || postingKey !== null}
                          title={chosenInvoice ? `Post contra against ${chosenInvoice.ref}` : "Pick an invoice first"}
                        >
                          {isPostingPicked ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <PlayCircle className="w-3 h-3 mr-1" />}
                          Post contra
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Unallocated payment matching tab ------------------------------------
//
// When a customer pays without saying which invoice the money is for, Merlin
// records the cash against the placeholder invoice "999999" with a NEGATIVE
// balance. This tab lists every such unallocated payment, paired (when
// unambiguous) with the unpaid invoice (ref starts with "I") on the same
// account whose value matches. One click posts a zero-cash allocation to
// Merlin — the invoice amount is posted and the negative 999999 balance is
// posted to offset it, so both rows become fully allocated, no bank movement.
//
// Source of truth is Merlin itself; there's no local table. After every post
// we refetch the list, so freshly-cleared pairs disappear.

interface UnallocatedMatchCandidate {
  account: string;
  customerid: string;
  unallocated: { ref: string; sltranid: string; amount: number; date: string | null };
  invoice: { ref: string; sltranid: string; amount: number } | null;
  ambiguousInvoiceRefs: string[];
  ambiguousInvoices: { ref: string; sltranid: string; amount: number }[];
  status: "ready" | "ambiguous" | "no-match";
}

function UnallocatedMatchingTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [postingKey, setPostingKey] = useState<string | null>(null);
  // For ambiguous rows: which candidate invoice the operator has picked,
  // keyed by the unallocated payment's sltranid.
  const [pickedInvoice, setPickedInvoice] = useState<Record<string, string>>({});

  const { data: matches = [], isLoading, isFetching, refetch, error } = useQuery<UnallocatedMatchCandidate[]>({
    queryKey: ["cash-receipts", "unallocated-matches"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/unallocated-matches`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      return res.json();
    },
    // The query hits Merlin — don't auto-refetch on focus, the user can press
    // Refresh explicitly. Stale-while-revalidate for 60s.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Post an allocation. For "ready" rows the invoice is m.invoice. For
  // "ambiguous" rows the operator picks one of m.ambiguousInvoices, passed as
  // `chosen`; operatorChosen tells the server to allow a pick among the
  // competing candidates (it still re-validates eligibility before posting).
  const postMatch = async (
    m: UnallocatedMatchCandidate,
    chosen?: { ref: string; sltranid: string; amount: number },
  ) => {
    const invoice = chosen ?? m.invoice;
    if (!invoice) return;
    const operatorChosen = Boolean(chosen);
    const key = `${m.unallocated.sltranid}:${invoice.sltranid}`;
    setPostingKey(key);
    try {
      const res = await fetch(`${BASE}/api/cash-receipts/unallocated-matches/post`, {
        method: "POST",
        credentials: "include",
        // Send the CSRF header explicitly rather than relying solely on the
        // global window.fetch interceptor (main.tsx). The server requires it
        // on every state-changing request (passesCsrfCheck); making it
        // explicit on this write path guarantees it survives regardless of
        // the interceptor's same-origin gating.
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({
          account: m.account,
          invoiceSltranid: invoice.sltranid,
          unallocatedSltranid: m.unallocated.sltranid,
          operatorChosen,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        toast({
          title: "Allocation failed",
          description: data.error || data.message || `Merlin returned code ${data.code ?? "unknown"}`,
          variant: "destructive",
        });
        return;
      }
      toast({
        title: `Allocated ${invoice.ref} against unallocated payment`,
        description: `£${invoice.amount.toFixed(2)} on ${m.account} (ref ${data.reference})`,
      });
      queryClient.invalidateQueries({ queryKey: ["cash-receipts", "unallocated-matches"] });
    } catch (err: any) {
      toast({ title: "Allocation failed", description: err?.message || "Network error", variant: "destructive" });
    } finally {
      setPostingKey(null);
    }
  };

  const ready = matches.filter((m) => m.status === "ready");
  const ambiguous = matches.filter((m) => m.status === "ambiguous");
  const noMatch = matches.filter((m) => m.status === "no-match");

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-semibold text-duffells-grey flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              Unallocated payment ↔ invoice matching
            </h2>
            <p className="text-xs text-duffells-grey/60 mt-1 max-w-3xl">
              Every unallocated payment in Merlin — cash recorded against invoice 999999 with a
              negative balance — paired (when the value matches a single unpaid invoice starting
              with "I" on the same account) with that invoice. Posting a pair sends a zero-value
              CashReceipt: the invoice amount is posted and the negative 999999 balance is posted
              to offset it, so both clear with no cash movement. Source of truth is Merlin; click
              Refresh after posting elsewhere to re-sync.
            </p>
          </div>
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Refresh
          </Button>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <AlertCircle className="w-4 h-4 inline mr-1.5" />
            {(error as any).message || "Failed to load unallocated payments"}
          </div>
        )}

        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-2xl font-semibold text-emerald-700">{ready.length}</div>
            <div className="text-xs text-emerald-700/80">Ready to allocate</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="text-2xl font-semibold text-amber-700">{ambiguous.length}</div>
            <div className="text-xs text-amber-700/80">Ambiguous (multiple matches)</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-2xl font-semibold text-slate-700">{noMatch.length}</div>
            <div className="text-xs text-slate-700/80">No matching invoice</div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : matches.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-sm text-duffells-grey/60">
          No unallocated payments in Merlin. Nothing to do.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-duffells-grey/70">
              <tr>
                <th className="px-4 py-3 text-left">Account</th>
                <th className="px-4 py-3 text-left">Unallocated payment</th>
                <th className="px-4 py-3 text-left">Invoice</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {matches.map((m) => {
                const key = `${m.unallocated.sltranid}:${m.invoice?.sltranid ?? "none"}`;
                const isPosting = postingKey === key;
                const pickedId = pickedInvoice[m.unallocated.sltranid] ?? "";
                const chosenInvoice = m.ambiguousInvoices.find((inv) => inv.sltranid === pickedId);
                const isPostingPicked = chosenInvoice
                  ? postingKey === `${m.unallocated.sltranid}:${chosenInvoice.sltranid}`
                  : false;
                return (
                  <tr key={`${m.account}-${m.unallocated.sltranid}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs text-duffells-grey">{m.account}</td>
                    <td className="px-4 py-3 font-mono text-xs text-red-700">
                      {m.unallocated.ref}
                      {m.unallocated.date && (
                        <span className="text-duffells-grey/50 ml-1.5">{m.unallocated.date}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {m.invoice ? (
                        <span className="text-emerald-700">{m.invoice.ref}</span>
                      ) : m.ambiguousInvoices.length > 0 ? (
                        <select
                          className="border border-amber-300 rounded-md px-2 py-1 text-xs font-mono bg-amber-50 text-amber-800 max-w-[16rem]"
                          value={pickedInvoice[m.unallocated.sltranid] ?? ""}
                          disabled={postingKey !== null}
                          onChange={(e) =>
                            setPickedInvoice((prev) => ({ ...prev, [m.unallocated.sltranid]: e.target.value }))
                          }
                          title={`${m.ambiguousInvoices.length} invoices on this account have a balance of £${m.unallocated.amount.toFixed(2)} — pick one to allocate`}
                        >
                          <option value="">Pick invoice…</option>
                          {m.ambiguousInvoices.map((inv) => (
                            <option key={inv.sltranid} value={inv.sltranid}>
                              {inv.ref} — £{inv.amount.toFixed(2)}
                            </option>
                          ))}
                        </select>
                      ) : m.ambiguousInvoiceRefs.length > 0 ? (
                        <span className="text-amber-700" title={`Multiple invoices on this account have a balance of £${m.unallocated.amount.toFixed(2)}`}>
                          {m.ambiguousInvoiceRefs.slice(0, 3).join(", ")}
                          {m.ambiguousInvoiceRefs.length > 3 && ` +${m.ambiguousInvoiceRefs.length - 3} more`}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{fmtMoney(m.unallocated.amount)}</td>
                    <td className="px-4 py-3 text-xs">
                      {m.status === "ready" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                          <CheckCircle2 className="w-3 h-3" /> Ready
                        </span>
                      )}
                      {m.status === "ambiguous" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                          <AlertCircle className="w-3 h-3" /> Ambiguous
                        </span>
                      )}
                      {m.status === "no-match" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                          No invoice
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {m.status === "ready" && (
                        <Button
                          size="sm"
                          onClick={() => postMatch(m)}
                          disabled={isPosting || postingKey !== null}
                        >
                          {isPosting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <PlayCircle className="w-3 h-3 mr-1" />}
                          Allocate
                        </Button>
                      )}
                      {m.status === "ambiguous" && m.ambiguousInvoices.length > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => chosenInvoice && postMatch(m, chosenInvoice)}
                          disabled={!chosenInvoice || postingKey !== null}
                          title={chosenInvoice ? `Allocate against ${chosenInvoice.ref}` : "Pick an invoice first"}
                        >
                          {isPostingPicked ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <PlayCircle className="w-3 h-3 mr-1" />}
                          Allocate
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
